import React, { useState, useEffect } from "react";
import usePageTitle from '../../hooks/usePageTitle';
import { Link } from "react-router-dom";
import api from "../../api/api";
import { isImpersonating, guardAction } from "../../utils/impersonate";
import NotificationBell from "../../components/NotificationBell";
import { fmt2, fmtPts, fmtPct, fmtK, THead, trHover } from '../../components/ui/shared';
import { TelegramNotifyCard, NotificationChannelsCard } from '../../components/analyst/NotificationCards';

const fmt = (n, d = 2) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: d });
// ── Virtual Account (Analyst Performance Simulation) ─────────────────────────
const VIRTUAL_INITIAL = 1000;
const VIRTUAL_RISK_PCT = 0.015; // 1.5% per trade = $15 fixed risk

function calcVirtual(signals) {
  const closed = (signals||[])
    .filter(s => s.status === "CLOSED_TP" || s.status === "CLOSED_SL")
    .slice()
    .sort((a, b) => new Date(a.issuedAt||a.closedAt||0) - new Date(b.issuedAt||b.closedAt||0));
  const risk = VIRTUAL_INITIAL * VIRTUAL_RISK_PCT; // $15 fixed
  let balance = VIRTUAL_INITIAL, peak = VIRTUAL_INITIAL, maxDD = 0;
  const curve = [{ t: null, v: VIRTUAL_INITIAL }];
  closed.forEach(s => {
    const e = parseFloat(s.entry), sl = parseFloat(s.sl), tp = parseFloat(s.tp);
    let rr = 1;
    if (e && sl && tp) {
      const rd = Math.abs(e - sl), rwd = Math.abs(tp - e);
      if (rd > 0) rr = rwd / rd;
    }
    balance = s.status === "CLOSED_TP"
      ? Math.max(0, balance + risk * rr)
      : Math.max(0, balance - risk);
    if (balance > peak) peak = balance;
    const dd = peak > 0 ? (peak - balance) / peak * 100 : 0;
    if (dd > maxDD) maxDD = dd;
    curve.push({ t: s.closedAt || s.issuedAt, v: balance });
  });
  return {
    balance,
    netPnL: balance - VIRTUAL_INITIAL,
    roi: (balance - VIRTUAL_INITIAL) / VIRTUAL_INITIAL * 100,
    maxDD,
    curve,
    closed: closed.length,
  };
}



function pipMult(pair="") {
  const p = pair.toUpperCase();
  if(p.includes("XAU")||p.includes("XAA")||p.includes("XPT")||p.includes("XPD")) return 10;
  if(p.includes("XAG")) return 1000;
  if(p.includes("OIL")||p.includes("NGAS")) return 100;
  if(p.includes("BTC")) return 1;
  if(p.includes("ETH")||p.includes("BNB")||p.includes("SOL")) return 10;
  if(p.includes("XRP")||p.includes("ADA")||p.includes("DOT")||p.includes("LTC")) return 1000;
  if(p.includes("US30")||p.includes("NAS100")||p.includes("SPX500")||p.includes("GER40")||p.includes("UK100")||p.includes("FRA40")||p.includes("AUS200")||p.includes("JPN225")||p.includes("HK50")) return 1;
  if(p.includes("JPY")) return 100;
  return 10000;
}
function pipUnit(pair="") {
  const p = pair.toUpperCase();
  if(p.includes("XAU")||p.includes("XAG")||p.includes("JPY")) return "pips";
  if(p.match(/EUR|GBP|AUD|NZD|USD|CAD|CHF/)) return "pips";
  return "pts";
}

const STATUS_CFG = {
  PENDING:          { bg: "rgba(79,140,255,0.15)",  color: "var(--accent)",     label: "PENDING" },
  RUNNING:          { bg: "rgba(254,202,87,0.15)",   color: "var(--warning)",    label: "RUNNING" },
  CLOSED_TP:        { bg: "rgba(29,209,161,0.15)",   color: "var(--success)",    label: "WIN " },
  CLOSED_SL:        { bg: "rgba(255,92,122,0.15)",   color: "var(--danger)",     label: "LOSS " },
  CANCELLED_MANUAL: { bg: "rgba(140,146,176,0.12)", color: "var(--text-muted)", label: "CANCELLED" },
  CANCELLED:        { bg: "rgba(140,146,176,0.12)", color: "var(--text-muted)", label: "CANCELLED" },
  PENDING_LIMIT:    { bg: "rgba(79,140,255,0.15)",  color: "var(--accent)",     label: "PENDING LIMIT" },
  OPEN:             { bg: "rgba(79,140,255,0.15)",   color: "var(--accent)",     label: "PENDING" },
  BRS_PENDING_BREAK: { bg: "rgba(252,211,77,0.15)",  color: "var(--warning)",    label: "WAITING BREAK" },
  BRS_ARMED:        { bg: "rgba(59,130,246,0.15)",  color: "var(--accent)",     label: "ARMED" },
  BRS_INVALIDATED:  { bg: "rgba(140,146,176,0.12)", color: "var(--text-muted)", label: "INVALIDATED" },
  EXPIRED:          { bg: "rgba(140,146,176,0.12)", color: "var(--text-muted)", label: "EXPIRED" },
};

const GRADE_COLOR = {
  "A+":"var(--success)","A":"var(--success)","A-":"var(--success)",
  "B+":"var(--accent)","B":"var(--accent)","B-":"var(--accent)",
  "C+":"var(--warning)","C":"var(--warning)","C-":"var(--warning)",
  "D":"var(--warning)","F":"var(--danger)",
};

const card = { background:"var(--bg-elevated)", borderRadius:"var(--radius-lg)", border:"1px solid var(--border-subtle)", overflow:"hidden" };
const pill = (cfg) => ({ display:"inline-block", padding:"3px 10px", borderRadius:999, fontSize:10, fontWeight:800, letterSpacing:0.4, background:cfg.bg, color:cfg.color });
const inputStyle = { width:"100%", background:"var(--bg-elevated-soft)", border:"1px solid var(--border-subtle)", borderRadius:"var(--radius-md)", padding:"10px 14px", fontSize:13, color:"var(--text-main)", outline:"none", boxSizing:"border-box" };
const selectStyle = { ...inputStyle, colorScheme:"dark" };
const btnPrimary = { padding:"10px 20px", borderRadius:"var(--radius-md)", border:"none", background:"var(--accent)", color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" };
const btnSecondary = { padding:"10px 20px", borderRadius:"var(--radius-md)", border:"1px solid var(--border-subtle)", background:"transparent", color:"var(--text-muted)", fontSize:13, fontWeight:700, cursor:"pointer" };
const btnSmall = (danger) => ({ padding:"4px 12px", borderRadius:6, border:`1px solid ${danger?"rgba(255,92,122,0.3)":"var(--border-subtle)"}`, background:danger?"rgba(255,92,122,0.08)":"var(--bg-elevated-soft)", color:danger?"var(--danger)":"var(--text-muted)", fontSize:11, fontWeight:700, cursor:"pointer" });

function Capital({ title, subtitle, onClose, children }) {
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <div style={{ background:"var(--bg-elevated)", borderRadius:"var(--radius-lg)", border:"1px solid var(--border-subtle)", width:"100%", maxWidth:460, maxHeight:"90vh", overflowY:"auto", boxShadow:"var(--shadow-soft)" }}>
        <div style={{ padding:"24px 28px", borderBottom:"1px solid var(--border-subtle)" }}>
          <div style={{ fontSize:16, fontWeight:800, color:"var(--text-main)" }}>{title}</div>
          {subtitle && <div style={{ fontSize:12, color:"var(--text-muted)", marginTop:4 }}>{subtitle}</div>}
        </div>
        <div style={{ padding:"24px 28px" }}>{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }) {
  return <div style={{ marginBottom:16 }}><label style={{ display:"block", fontSize:10, fontWeight:800, color:"var(--text-muted)", letterSpacing:0.8, textTransform:"uppercase", marginBottom:8 }}>{label}</label>{children}</div>;
}
function ErrBox({ msg }) {
  return msg ? <div style={{ background:"rgba(255,92,122,0.1)", border:"1px solid rgba(255,92,122,0.3)", borderRadius:"var(--radius-md)", padding:"10px 14px", color:"var(--danger)", fontSize:12, marginBottom:16 }}>{msg}</div> : null;
}
function CapitalFooter({ onClose, onSubmit, loading, submitLabel }) {
  return (
    <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:24, paddingTop:20, borderTop:"1px solid var(--border-subtle)" }}>
      <button style={btnSecondary} onClick={onClose}>Cancel</button>
      <button style={{ ...btnPrimary, opacity:loading?0.6:1 }} onClick={onSubmit} disabled={loading}>{loading?"Saving...":submitLabel}</button>
    </div>
  );
}

function CreateSignalSetCapital({ currentCount, onClose, onSuccess }) {
  const [name,setName]=useState(""); const [desc,setDesc]=useState(""); const [loading,setLoading]=useState(false); const [error,setError]=useState(null);
  const submit=async()=>{ if(!name.trim()){setError("Name required");return;} try{setLoading(true);await api.createAnalystSignalSet({name:name.trim(),description:desc.trim()});onSuccess();}catch(e){setError(e.message);}finally{setLoading(false);} };
  return <Capital title="Create Signal Set" subtitle={`Brand name as signal provider. Max 5. (${currentCount}/5 used)`} onClose={onClose}><ErrBox msg={error}/><Field label="Signal Set Name *"><input style={inputStyle} placeholder="e.g. Gold Swing Alpha" value={name} onChange={e=>setName(e.target.value)}/></Field><Field label="Strategy Description"><textarea style={{...inputStyle,height:72,resize:"vertical"}} placeholder="e.g. Swing trading XAUUSD using structure breaks and confluence zones." value={desc} onChange={e=>setDesc(e.target.value)}/></Field><CapitalFooter onClose={onClose} onSubmit={submit} loading={loading} submitLabel="Create"/></Capital>;
}
function EditSignalSetCapital({ signalSet, onClose, onSuccess }) {
  const [name,setName]=useState(signalSet.name); const [desc,setDesc]=useState(signalSet.description||""); const [loading,setLoading]=useState(false); const [error,setError]=useState(null);
  const [allowManualFollow,setAllowManualFollow]=useState(signalSet.allowManualFollow??false);
  const [manualFee,setManualFee]=useState(signalSet.manualSubscriptionFee??20);
  const submit=async()=>{
    if(!name.trim()){setError("Name required");return;}
    if(allowManualFollow&&(!manualFee||parseFloat(manualFee)<20)){setError("Manual follow fee must be at least $20/month");return;}
    try{
      setLoading(true);
      await api.updateAnalystSignalSet(signalSet.id,{name:name.trim(),description:desc.trim(),allowManualFollow,manualSubscriptionFee:parseFloat(manualFee)});
      onSuccess();
    }catch(e){setError(e.message);}finally{setLoading(false);}
  };
  return (
    <Capital title="Edit Signal Set" onClose={onClose}>
      <ErrBox msg={error}/>
      <Field label="Signal Set Name *"><input style={inputStyle} value={name} onChange={e=>setName(e.target.value)}/></Field>
      <Field label="Strategy Description"><textarea style={{...inputStyle,height:72,resize:"vertical"}} placeholder="e.g. Swing trading XAUUSD using structure breaks and confluence zones." value={desc} onChange={e=>setDesc(e.target.value)}/></Field>
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,padding:"10px 14px",borderRadius:"var(--radius-md)",background:"var(--bg-elevated-soft)",border:"1px solid var(--border-subtle)"}}>
        <input type="checkbox" checked={allowManualFollow} onChange={e=>setAllowManualFollow(e.target.checked)} style={{width:16,height:16,cursor:"pointer"}}/>
        <div style={{flex:1}}>
          <div style={{fontSize:12,fontWeight:700,color:"var(--text-main)"}}>Allow Manual Follow</div>
          <div style={{fontSize:11,color:"var(--text-muted)"}}>Investors can follow without connecting a live account (flat subscription, no performance fee)</div>
        </div>
      </div>
      {allowManualFollow&&(
        <Field label="Manual Follow Price (USD/month, min $20)">
          <input style={inputStyle} type="number" step="1" min="20" value={manualFee} onChange={e=>setManualFee(e.target.value)}/>
        </Field>
      )}
      <CapitalFooter onClose={onClose} onSubmit={submit} loading={loading} submitLabel="Save Changes"/>
    </Capital>
  );
}

function EditSignalCapital({ signal, onClose, onSuccess }) {
  const [form,setForm]=useState({entry:signal.entry,sl:signal.sl,tp:signal.tp,notes:signal.notes||""});
  const [loading,setLoading]=useState(false); const [error,setError]=useState(null);
  const s=(k,v)=>setForm(f=>({...f,[k]:v}));
  const rr=(()=>{ const e=parseFloat(form.entry),sl=parseFloat(form.sl),t=parseFloat(form.tp); if(!e||!sl||!t)return null; const risk=signal.direction==="BUY"?e-sl:sl-e,reward=signal.direction==="BUY"?t-e:e-t; if(risk<=0)return null; return(reward/risk).toFixed(2); })();
  const rrOk=rr&&parseFloat(rr)>=1.0;
  const submit=async()=>{ if(!form.entry||!form.sl||!form.tp){setError("Entry, SL and TP required");return;} if(rr&&parseFloat(rr)<1.0){setError("R:R must be at least 1:1");return;} try{setLoading(true);await api.updateAnalystSignal(signal.id,{entry:form.entry,sl:form.sl,tp:form.tp,notes:form.notes});onSuccess();}catch(e){setError(e.message);}finally{setLoading(false);} };
  return (
    <Capital title={`Edit Signal #${signal.id}`} subtitle={`${signal.pair} · ${signal.direction} · PENDING only`} onClose={onClose}>
      <ErrBox msg={error}/>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(120px,1fr))", gap:12, marginBottom:8 }}>
        {[["entry","Entry *"],["sl","Stop Loss *"],["tp","Take Profit *"]].map(([k,l])=><Field key={k} label={l}><input style={inputStyle} type="number" step="any" value={form[k]} onChange={e=>s(k,e.target.value)}/></Field>)}
      </div>
      {rr&&<div style={{ marginBottom:16, padding:"10px 14px", borderRadius:"var(--radius-md)", background:rrOk?"rgba(29,209,161,0.1)":"rgba(255,92,122,0.1)", border:`1px solid ${rrOk?"rgba(29,209,161,0.3)":"rgba(255,92,122,0.3)"}` }}><span style={{ fontSize:13, fontWeight:800, color:rrOk?"var(--success)":"var(--danger)" }}>R:R = 1:{rr} {rrOk?"":" Min 1:1"}</span></div>}
      <Field label="Notes"><textarea style={{...inputStyle,height:60,resize:"vertical"}} value={form.notes} onChange={e=>s("notes",e.target.value)}/></Field>
      <CapitalFooter onClose={onClose} onSubmit={submit} loading={loading} submitLabel="Save Changes"/>
    </Capital>
  );
}

const TH=({children,right})=><th style={{ padding:"8px 10px", fontSize:10, fontWeight:800, color:"var(--text-muted)", letterSpacing:0.8, textTransform:"uppercase", textAlign:right?"right":"left", borderBottom:"1px solid var(--border-subtle)", whiteSpace:"nowrap" }}>{children}</th>;
const TD=({children,right,style})=><td style={{ padding:"8px 10px", fontSize:12, color:"var(--text-muted)", borderBottom:"1px solid var(--border-subtle)", textAlign:right?"right":"left", verticalAlign:"middle", ...style }}>{children}</td>;

function SignalTable({ signals, showActions, onEdit, onCancel, cancelId }) {
  if(signals.length===0) return(
    <div style={{ padding:"24px 16px", textAlign:"center", color:"var(--text-muted)" }}>
      <div style={{ fontSize:13, fontWeight:600 }}>No signals here</div>
    </div>
  );
  const [page,setPage]=React.useState(0);
  const PAGE_SIZE=20;
  const totalPages=Math.ceil(signals.length/PAGE_SIZE);
  const pagedSignals=signals.slice(page*PAGE_SIZE,(page+1)*PAGE_SIZE);
  return(
    <div>
    <div style={{overflowX:"auto"}}><table style={{ width:"100%", borderCollapse:"collapse", minWidth:500 }}>
      <thead><tr style={{ background:"var(--bg-elevated-soft)" }}>
        <TH>#</TH><TH>Pair</TH><TH>Dir</TH><TH>Status</TH>
        <TH right>Entry</TH><TH right>SL</TH><TH right>TP</TH>
        <TH right>Pips</TH><TH>Open Time</TH><TH>Close Time</TH>
        {showActions&&<TH>Actions</TH>}
      </tr></thead>
      <tbody>
        {pagedSignals.map((s,idx)=>{
          const cfg=STATUS_CFG[s.status]||STATUS_CFG.OPEN;
          return(
            <tr key={s.id} onMouseEnter={e=>e.currentTarget.style.background="var(--bg-elevated-soft)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
              <TD style={{ color:"var(--text-muted)" }}>#{page*PAGE_SIZE+idx+1}</TD>
              <TD><div style={{ fontWeight:800, letterSpacing:0.3 }}>{s.pair||"-"}</div></TD>
              <TD><span style={{ fontWeight:900, color:s.direction==="BUY"?"var(--success)":"var(--danger)", letterSpacing:0.5 }}>{s.direction}</span></TD>
              <TD><span style={pill(cfg)}>{cfg.label}</span></TD>
              <TD right style={{ fontFamily:"monospace" }}>{s.entry}</TD>
              <TD right style={{ fontFamily:"monospace", color:"var(--danger)" }}>{s.sl}</TD>
              <TD right style={{ fontFamily:"monospace", color:"var(--success)" }}>{s.tp}</TD>
              <TD right style={{fontWeight:700,color:s.status==="CLOSED_TP"?"var(--success)":s.status==="CLOSED_SL"?"var(--danger)":s.status==="RUNNING"?"var(--accent)":"var(--text-muted)"}}>
                {(()=>{
                  if(s.status==="CLOSED_TP"||s.status==="CLOSED_SL"){
                    const closeRef=s.status==="CLOSED_TP"?parseFloat(s.tp):parseFloat(s.sl);
                    const val=s.pips!=null?Math.abs(s.pips):Math.abs(closeRef-parseFloat(s.entry))*pipMult(s.pair);
                    const sign=s.status==="CLOSED_TP"?"+":"-";
                    const unit=pipUnit(s.pair);
                    return <span>{sign}{Math.round(val*10)/10} <span style={{fontSize:9,color:"var(--text-muted)",fontWeight:600}}>{unit}</span></span>;
                  }
                  if(s.status==="RUNNING"&&s.currentPrice&&parseFloat(s.currentPrice)>0){
                    const cur=parseFloat(s.currentPrice), e=parseFloat(s.entry);
                    const raw=s.direction==="BUY"?cur-e:e-cur;
                    const pips=Math.round(raw*pipMult(s.pair)*10)/10;
                    const unit=pipUnit(s.pair);
                    const col=pips>=0?"var(--success)":"var(--danger)";
                    return <span style={{color:col,fontWeight:700}}>{pips>=0?"+":""}{pips}</span>;
                  }
                  return <span style={{color:"var(--text-muted)"}}>—</span>;
                })()}
              </TD>
              <TD style={{ color:"var(--text-muted)", fontSize:11 }}>{s.issuedAt?s.issuedAt.replace("T"," ").replace(/\+00:00|\.000Z|Z$/,""):" -"}</TD>
              <TD style={{ color:"var(--text-muted)", fontSize:11 }}>{s.closedAt?s.closedAt.replace("T"," ").replace(/\+00:00|\.000Z|Z$/,""):"-"}</TD>
              {showActions&&<TD>
                <div style={{ display:"flex", gap:6 }}>
                  {(s.status==="PENDING"||s.status==="OPEN"||s.status==="BRS_PENDING_BREAK"||s.status==="BRS_ARMED")&&<>
                    <button style={btnSmall(false)} onClick={()=>onEdit(s)}>Edit</button>
                    <button style={btnSmall(true)} onClick={()=>onCancel(s.id)} disabled={cancelId===s.id}>{cancelId===s.id?"...":"Cancel"}</button>
                  </>}
                  {s.status==="RUNNING"&&<span style={{ fontSize:11, color:"var(--warning)", fontWeight:700 }}>Locked</span>}
                </div>
              </TD>}
            </tr>
          );
        })}
      </tbody>
    </table></div>
    {totalPages>1&&(
      <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:8,padding:"12px 0"}}>
        <button onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{padding:"4px 12px",borderRadius:"var(--radius-sm)",border:"1px solid var(--border-subtle)",background:"var(--bg-elevated)",color:page===0?"var(--text-muted)":"var(--text-main)",cursor:page===0?"default":"pointer",fontSize:12}}>Prev</button>
        <span style={{fontSize:12,color:"var(--text-muted)",fontWeight:700}}>Page {page+1} / {totalPages} · {signals.length} signals</span>
        <button onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page===totalPages-1} style={{padding:"4px 12px",borderRadius:"var(--radius-sm)",border:"1px solid var(--border-subtle)",background:"var(--bg-elevated)",color:page===totalPages-1?"var(--text-muted)":"var(--text-main)",cursor:page===totalPages-1?"default":"pointer",fontSize:12}}>Next</button>
      </div>
    )}
    </div>
  );
}

// ── Send Signal Tab — Split Pane ─────────────────────────────────────────────
function ExecutionAccountCard({ signalSets }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(null); // account object or null
  const [showLinkModal, setShowLinkModal] = useState(null); // account object or null
  const [showEditModal, setShowEditModal] = useState(null); // account object or null
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const d = await api.getSelfExecutionStatus();
      setStatus(d.accounts || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cardStyle = {
    background: "var(--bg-surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--radius-lg)",
    padding: "14px 16px",
    marginBottom: 14,
    width: "100%",
  };

  if (loading) {
    return <div style={{ ...cardStyle, color: "var(--text-muted)", fontSize: 13 }}>Loading execution account...</div>;
  }

  const accounts = status || [];

  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: accounts.length ? 12 : 0 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-main)" }}>Live Execution Account</div>
          <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
            Connect your own real MT4/5 account(s) to auto-execute your signals via EA Investor.
            These accounts are not tracked for performance and never appear on the marketplace.
          </div>
        </div>
      </div>

      {error && <div style={{ fontSize: 12, color: "var(--danger)", marginTop: 8 }}>{error}</div>}

      {accounts.length === 0 ? (
        <button style={{ ...btnPrimary, marginTop: 12 }} onClick={() => setShowAddModal(true)}>
          + Connect Execution Account
        </button>
      ) : (
        <div>
          {accounts.map(account => (
          <div key={account.accountId} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid var(--border-subtle)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 12px", background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>{account.nickname || account.accountNumber}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{account.broker} · {account.platform} · #{account.accountNumber}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                Risk <span style={{ fontWeight: 700, color: "var(--text-main)" }}>{(parseFloat(account.riskLevel) > 0 ? parseFloat(account.riskLevel) : 1.0)}%</span>
              </div>
              {account.setId ? (
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--success)" }}>Connected — {account.setName}</span>
              ) : (
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>Not linked</span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button style={{ ...btnSecondary, fontSize: 12, padding: "7px 12px" }} onClick={() => setShowKeyModal(account)}>
              Generate EA Key
            </button>
            {account.setId ? (
              <button style={{ ...btnSecondary, fontSize: 12, padding: "7px 12px", color: "var(--danger)" }}
                onClick={async () => { if (window.confirm("Unlink this account from " + account.setName + "?")) { await api.unlinkSelfExecutionAccount(account.linkId); load(); } }}>
                Unlink
              </button>
            ) : (
              <button style={{ ...btnPrimary, fontSize: 12, padding: "7px 12px" }} onClick={() => setShowLinkModal(account)}>
                Link to Signal Set
              </button>
            )}
            <button style={{ ...btnSecondary, fontSize: 12, padding: "7px 12px" }} onClick={() => setShowEditModal(account)}>
              Edit Account
            </button>
            {!account.setId && (
              <button style={{ ...btnSecondary, fontSize: 12, padding: "7px 12px", color: "var(--danger)" }}
                onClick={async () => { if (window.confirm("Permanently delete " + (account.nickname || account.accountNumber) + "? This cannot be undone.")) { await api.deleteSelfExecutionAccount(account.accountId); load(); } }}>
                Delete
              </button>
            )}
          </div>
          </div>
          ))}
          <button style={{ ...btnSecondary, fontSize: 12, padding: "8px 16px" }} onClick={() => setShowAddModal(true)}>
            + Connect Another Account
          </button>
        </div>
      )}

      {showAddModal && (
        <ExecAddAccountModal onClose={() => setShowAddModal(false)} onDone={() => { setShowAddModal(false); load(); }} />
      )}
      {showKeyModal && (
        <ExecEAKeyModal account={showKeyModal} onClose={() => setShowKeyModal(null)} />
      )}
      {showLinkModal && (
        <ExecLinkModal account={showLinkModal} signalSets={signalSets} onClose={() => setShowLinkModal(null)} onDone={() => { setShowLinkModal(null); load(); }} />
      )}
      {showEditModal && (
        <ExecEditAccountModal account={showEditModal} onClose={() => setShowEditModal(null)} onDone={() => { setShowEditModal(null); load(); }} />
      )}
    </div>
  );
}

function ExecModalShell({ title, subtitle, children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 }}>
      <div style={{ background: "var(--bg-surface)", border: "1px solid var(--border)", borderRadius: "var(--radius-xl)", width: "100%", maxWidth: 420, padding: 24 }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--text-main)", marginBottom: 4 }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 16 }}>{subtitle}</div>}
        {children}
      </div>
    </div>
  );
}

function ExecAddAccountModal({ onClose, onDone }) {
  const [form, setForm] = useState({ account_number: "", broker: "", platform: "MT5", server: "", investor_password: "", owner_api_key: "", nickname: "", is_cent: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [needsOwnerKey, setNeedsOwnerKey] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.account_number || !form.broker || !form.server) {
      setError("Account Number, Broker and Server are required");
      return;
    }
    if (!needsOwnerKey && !form.investor_password) {
      setError("Investor Password is required");
      return;
    }
    if (needsOwnerKey && (!form.investor_password || !form.owner_api_key)) {
      setError("Both the Investor Password and Owner's API Key are required to link this account");
      return;
    }
    setLoading(true);
    try {
      await api.createSelfExecutionAccount(form);
      onDone();
    } catch (e) {
      if (e.message && e.message.includes("different CrunchAlpha login")) {
        setNeedsOwnerKey(true);
      }
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExecModalShell title="Connect Execution Account" subtitle="This account is for your own signal execution only — not tracked, not published." onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Account Number</label>
          <input required style={inputStyle} value={form.account_number} onChange={e => set("account_number", e.target.value)} placeholder="e.g. 2055822" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Broker</label>
          <input required style={inputStyle} value={form.broker} onChange={e => set("broker", e.target.value)} placeholder="e.g. Exness" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Platform</label>
          <div style={{ display: "flex", gap: 8 }}>
            {["MT4", "MT5"].map(p => (
              <button key={p} type="button" style={{ ...btnSecondary, flex: 1, ...(form.platform === p ? { color: "var(--accent)", borderColor: "var(--accent-border)" } : {}) }} onClick={() => set("platform", p)}>{p}</button>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Server</label>
          <input required style={inputStyle} value={form.server} onChange={e => set("server", e.target.value)} placeholder="e.g. Exness-Real5" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Account Type</label>
          <div style={{ display: "flex", gap: 8 }}>
            {[["standard", "Standard (USD)"], ["cent", "Cent (USC)"]].map(([v, l]) => (
              <button key={v} type="button"
                style={{ ...btnSecondary, flex: 1, fontSize: 12, ...((v === "cent") === form.is_cent ? { color: "var(--accent)", borderColor: "var(--accent-border)" } : {}) }}
                onClick={() => set("is_cent", v === "cent")}>{l}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>Cent accounts: 1 USD = 100 USC</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Investor Password</label>
          <input type="password" style={inputStyle} value={form.investor_password} onChange={e => set("investor_password", e.target.value)} placeholder="Read-only password" />
        </div>
        {needsOwnerKey && (
          <div style={{ marginBottom: 12, padding: 12, borderRadius: "var(--radius-md)", background: "rgba(78,154,241,0.07)", border: "1px solid rgba(78,154,241,0.25)" }}>
            <div style={{ fontSize: 12, color: "var(--text-main)", fontWeight: 600, marginBottom: 4 }}>This account belongs to a different CrunchAlpha login</div>
            <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5, marginBottom: 10 }}>
              This account's Investor Password AND one of the owner's active API keys (starts with <b>crunch_</b>, found under their EA Publisher setup) are both required to verify and link it here.
            </div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Owner's API Key</label>
            <input style={inputStyle} value={form.owner_api_key} onChange={e => set("owner_api_key", e.target.value)} placeholder="crunch_..." />
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Nickname (optional)</label>
          <input style={inputStyle} value={form.nickname} onChange={e => set("nickname", e.target.value)} placeholder="e.g. My Execution Account" />
        </div>
        {error && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" style={{ ...btnSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...btnPrimary, flex: 2, opacity: loading ? 0.6 : 1 }} disabled={loading}>{loading ? "Connecting..." : "Connect Account"}</button>
        </div>
      </form>
    </ExecModalShell>
  );
}

function ExecEditAccountModal({ account, onClose, onDone }) {
  const [form, setForm] = useState({
    nickname: account.nickname || "",
    broker: account.broker || "",
    server: "",
    investor_password: "",
    riskLevel: String(parseFloat(account.riskLevel) > 0 ? parseFloat(account.riskLevel) : 1.0),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    const riskVal = parseFloat(form.riskLevel);
    if (!riskVal || riskVal <= 0) {
      setError("Risk % per trade must be a positive number");
      return;
    }
    setLoading(true);
    try {
      await api.updateSelfExecutionAccount(account.accountId, {
        nickname: form.nickname,
        broker: form.broker,
        server: form.server,
        investor_password: form.investor_password,
      });
      await api.setSelfExecutionRiskLevel({ account_id: account.accountId, risk_level: String(riskVal) });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExecModalShell title="Edit Execution Account" subtitle="Account number and platform cannot be changed after creation." onClose={onClose}>
      <form onSubmit={submit}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Nickname</label>
          <input style={inputStyle} value={form.nickname} onChange={e => set("nickname", e.target.value)} placeholder="e.g. My Execution Account" />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Risk % per trade</label>
          <input type="number" min="0.1" max="100" step="0.1" style={inputStyle} value={form.riskLevel} onChange={e => set("riskLevel", e.target.value)} />
          <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 4 }}>% of account equity risked per trade, based on entry/SL distance. E.g. 1.5 = 1.5% risk per trade.</div>
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Broker</label>
          <input style={inputStyle} value={form.broker} onChange={e => set("broker", e.target.value)} placeholder={account.broker} />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Server <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>(leave blank to keep unchanged)</span></label>
          <input style={inputStyle} value={form.server} onChange={e => set("server", e.target.value)} placeholder="Current server unchanged" />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Investor Password <span style={{ color: "var(--text-faint)", fontWeight: 400 }}>(leave blank to keep unchanged)</span></label>
          <input type="password" style={inputStyle} value={form.investor_password} onChange={e => set("investor_password", e.target.value)} placeholder="Current password unchanged" />
        </div>
        {error && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
        <div style={{ display: "flex", gap: 10 }}>
          <button type="button" style={{ ...btnSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...btnPrimary, flex: 2, opacity: loading ? 0.6 : 1 }} disabled={loading}>{loading ? "Saving..." : "Save Changes"}</button>
        </div>
      </form>
    </ExecModalShell>
  );
}

function ExecEAKeyModal({ account, onClose }) {
  const [mt5Account, setMt5Account] = useState(account.accountNumber || "");
  const [key, setKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    setError(null);
    setLoading(true);
    try {
      const d = await api.generateSelfExecutionEAKey({ account_id: account.accountId, mt5_account: mt5Account, platform: account.platform || "MT5" });
      setKey(d.eaKey);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExecModalShell title="Generate EA Investor Key" subtitle="Use this key in the EA Investor Expert Advisor on your MT4/5 terminal." onClose={onClose}>
      {!key ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>MT5 Account Number</label>
            <input style={inputStyle} value={mt5Account} onChange={e => setMt5Account(e.target.value)} />
          </div>
          {error && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...btnSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
            <button style={{ ...btnPrimary, flex: 2, opacity: loading ? 0.6 : 1 }} onClick={generate} disabled={loading}>{loading ? "Generating..." : "Generate Key"}</button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ padding: "12px 14px", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-main)", wordBreak: "break-all", marginBottom: 12, fontFamily: "monospace" }}>
            {key}
          </div>
          <div style={{ fontSize: 11, color: "var(--danger)", marginBottom: 16 }}>Save this key now — it will not be shown again.</div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 8 }}>Download EA Investor</div>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <a href="/api/ea/download/mt5" style={{ ...btnSecondary, flex: 1, fontSize: 12, padding: "8px 0", textAlign: "center", textDecoration: "none", display: "block" }}>
              MT5 (.ex5)
            </a>
            <a href="/api/ea/download/mt4" style={{ ...btnSecondary, flex: 1, fontSize: 12, padding: "8px 0", textAlign: "center", textDecoration: "none", display: "block" }}>
              MT4 (.ex4)
            </a>
          </div>
          <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 16 }}>
            Copy the .ex4/.ex5 file to your terminal's Experts folder, attach it to your chart, enter this key, and enable AutoTrading.
          </div>
          <button style={btnPrimary} onClick={onClose}>Done</button>
        </div>
      )}
    </ExecModalShell>
  );
}

function ExecLinkModal({ account, signalSets, onClose, onDone }) {
  const [setId, setSetId] = useState(signalSets[0]?.id || "");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setError(null);
    if (!setId) { setError("Select a signal set"); return; }
    setLoading(true);
    try {
      await api.linkSelfExecutionAccount({ set_id: setId, account_id: account.accountId });
      onDone();
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ExecModalShell title="Link to Signal Set" subtitle="Your signals from this set will auto-execute on this account." onClose={onClose}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Signal Set</label>
        <select style={inputStyle} value={setId} onChange={e => setSetId(e.target.value)}>
          {signalSets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>
      {error && <div style={{ fontSize: 12, color: "var(--danger)", marginBottom: 12 }}>{error}</div>}
      <div style={{ display: "flex", gap: 10 }}>
        <button style={{ ...btnSecondary, flex: 1 }} onClick={onClose}>Cancel</button>
        <button style={{ ...btnPrimary, flex: 2, opacity: loading ? 0.6 : 1 }} onClick={submit} disabled={loading}>{loading ? "Linking..." : "Link Account"}</button>
      </div>
    </ExecModalShell>
  );
}

function SendSignalPane({ signalSets, selectedSetId, onSuccess }) {
  const [mode, setMode] = useState("PENDING"); // "PENDING" | "BRS"
  const [form, setForm] = useState({
    setId: selectedSetId || signalSets[0]?.id || "",
    pair: "XAUUSD",
    direction: "BUY",
    entry: "",
    sl: "",
    tp: "",
    notes: "",
    rrInput: "",
    breakPrice: "",
    breakType: "WICK",
    breakTimeframe: "",
    entries: [""],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [sent, setSent]       = useState(false);

  useEffect(() => {
    if (selectedSetId) s("setId", selectedSetId);
  }, [selectedSetId]);

  const s = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const setEntryAt = (idx, val) => {
    setForm(f => {
      const next = [...f.entries];
      next[idx] = val;
      return { ...f, entries: next };
    });
  };
  const addEntry = () => {
    setForm(f => f.entries.length >= 5 ? f : { ...f, entries: [...f.entries, ""] });
  };
  const removeEntry = (idx) => {
    setForm(f => f.entries.length <= 1 ? f : { ...f, entries: f.entries.filter((_, i) => i !== idx) });
  };

  const referenceEntry = mode === "BRS" ? (form.entries[0] || "") : form.entry;

  const rr = (() => {
    const e = parseFloat(referenceEntry), sl = parseFloat(form.sl), t = parseFloat(form.tp);
    if (!e || !sl || !t) return null;
    const risk   = form.direction === "BUY" ? e - sl : sl - e;
    const reward = form.direction === "BUY" ? t - e  : e - t;
    if (risk <= 0) return null;
    return (reward / risk).toFixed(2);
  })();
  const rrFloor = mode === "BRS" ? 0.5 : 1.0;
  const rrOk = rr && parseFloat(rr) >= rrFloor;

  const submit = async () => {
    setError(null);
    if (!form.setId) { setError("Pilih Signal Set"); return; }

    if (mode === "PENDING") {
      if (!form.entry||!form.sl||!form.tp) { setError("Entry, SL dan TP wajib diisi"); return; }
      if (rr && parseFloat(rr) < 1.0)     { setError("Minimum R:R adalah 1:1"); return; }
      try {
        setLoading(true);
        await api.createAnalystSignal({
          setId:     form.setId,
          pair:      form.pair,
          direction: form.direction,
          entry:     form.entry,
          sl:        form.sl,
          tp:        form.tp,
          notes:     form.notes,
        });
        setSent(true);
        setTimeout(() => {
          setSent(false);
          setForm(f => ({ ...f, entry:"", sl:"", tp:"", notes:"", rrInput:"" }));
          onSuccess();
        }, 1800);
      } catch(e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    // mode === "BRS"
    const cleanEntries = form.entries.map(e => e.trim()).filter(Boolean);
    if (!form.breakPrice)                    { setError("Break Price wajib diisi"); return; }
    if (!form.breakType)                     { setError("Break Type wajib dipilih"); return; }
    if (form.breakType === "CLOSE" && !form.breakTimeframe) { setError("Timeframe wajib dipilih untuk Break Type CLOSE"); return; }
    if (cleanEntries.length === 0)           { setError("Minimal 1 Entry Price"); return; }
    if (!form.sl || !form.tp)                { setError("SL dan TP wajib diisi"); return; }
    if (rr && parseFloat(rr) < 0.5)          { setError("Minimum R:R adalah 0.5"); return; }
    try {
      setLoading(true);
      await api.createAnalystSignal({
        setId:      form.setId,
        pair:       form.pair,
        direction:  form.direction,
        mode:       "BRS",
        breakPrice: form.breakPrice,
        breakType:  form.breakType,
        breakTimeframe: form.breakTimeframe,
        entries:    cleanEntries,
        sl:         form.sl,
        tp:         form.tp,
        notes:      form.notes,
      });
      setSent(true);
      setTimeout(() => {
        setSent(false);
        setForm(f => ({ ...f, sl:"", tp:"", notes:"", rrInput:"", breakPrice:"", breakTimeframe:"", entries:[""] }));
        onSuccess();
      }, 1800);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const PAIRS_LIST = [
    {g:"Commodity",   o:["XAUUSD","XAGUSD","XPTUSD","XPDUSD"]},
    {g:"Forex Major", o:["EURUSD","GBPUSD","USDJPY","AUDUSD","USDCAD","USDCHF","NZDUSD"]},
    {g:"Forex Minor", o:["EURGBP","EURJPY","GBPJPY","AUDJPY","CADJPY","NZDJPY","CHFJPY"]},
    {g:"Index",       o:["US30","NAS100","SPX500","GER40","UK100","JPN225"]},
    {g:"Crypto",      o:["BTCUSD","ETHUSD","BNBUSD","XRPUSD","SOLUSD"]},
    {g:"Energy",      o:["USOIL","UKOIL","NATGAS"]},
  ];

  return (
    <div style={{ display:"grid", gridTemplateColumns:"minmax(0,1fr) minmax(260px,340px)", gap:14, alignItems:"start" }}>
    <div style={{
      padding:"14px 16px",
      display:"flex",
      flexDirection:"column",
      gap:0,
      background:"var(--bg-surface)",
      border:"1px solid var(--border)",
      borderRadius:"var(--radius-lg)",
      minWidth:0,
    }}>
      {/* Header */}
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:14, fontWeight:800, color:"var(--text-main)" }}>Send Signal</div>
      </div>

      {/* Mode toggle */}
      <div style={{ display:"flex", gap:8, marginBottom:18 }}>
        {[
          {k:"PENDING", label:"Direct Signal"},
          {k:"BRS",     label:"Break & Retrace Signal"},
        ].map(m => (
          <button key={m.k} onClick={() => setMode(m.k)} style={{
            flex:1, padding:"10px", borderRadius:"var(--radius-md)", border:"1px solid",
            borderColor: mode===m.k ? "var(--accent)" : "var(--border-subtle)",
            background:  mode===m.k ? "rgba(78,154,241,0.12)" : "transparent",
            color:       mode===m.k ? "var(--accent)" : "var(--text-muted)",
            fontSize:13, fontWeight:800, cursor:"pointer",
          }}>{m.label}</button>
        ))}
      </div>

      {sent && (
        <div style={{ padding:"12px 14px", borderRadius:"var(--radius-md)", background:"rgba(29,209,161,0.1)", border:"1px solid rgba(29,209,161,0.3)", marginBottom:16, fontSize:13, fontWeight:700, color:"var(--success)", textAlign:"center" }}>
             Signal terkirim!
        </div>
      )}

      <ErrBox msg={error} />

      {/* Signal Set */}
      <Field label="Signal Set *">
        <select style={selectStyle} value={form.setId} onChange={e => s("setId", e.target.value)}>
          {signalSets.map(ss => <option key={ss.id} value={ss.id}>{ss.name}</option>)}
        </select>
      </Field>

      {/* Pair */}
      <Field label="Pair *">
        <select style={selectStyle} value={form.pair} onChange={e => s("pair", e.target.value)}>
          {PAIRS_LIST.map(({g, o}) => (
            <optgroup key={g} label={g}>
              {o.map(p => <option key={p} value={p}>{p}</option>)}
            </optgroup>
          ))}
        </select>
      </Field>

      {/* Direction */}
      <Field label="Direction *">
        <div style={{ display:"flex", gap:8 }}>
          {["BUY","SELL"].map(d => (
            <button key={d} onClick={() => s("direction", d)} style={{
              flex:1, padding:"10px", borderRadius:"var(--radius-md)", border:"1px solid",
              borderColor: form.direction===d ? (d==="BUY"?"var(--success)":"var(--danger)") : "var(--border-subtle)",
              background:  form.direction===d ? (d==="BUY"?"rgba(29,209,161,0.12)":"rgba(255,92,122,0.12)") : "transparent",
              color:       form.direction===d ? (d==="BUY"?"var(--success)":"var(--danger)") : "var(--text-muted)",
              fontSize:14, fontWeight:900, cursor:"pointer",
            }}>{d}</button>
          ))}
        </div>
      </Field>

      {mode === "PENDING" && (
        <Field label="Entry *">
          <input
            style={inputStyle}
            type="number" step="any" placeholder="Entry price"
            value={form.entry} onChange={e => s("entry", e.target.value)}
          />
        </Field>
      )}

      {mode === "BRS" && (
        <>
          <Field label="Break Price *">
            <input
              style={inputStyle}
              type="number" step="any" placeholder="e.g. 4390.00"
              value={form.breakPrice} onChange={e => s("breakPrice", e.target.value)}
            />
          </Field>

          <Field label="Break Type *">
            <select style={selectStyle} value={form.breakType} onChange={e => s("breakType", e.target.value)}>
              <option value="WICK">WICK — price touches level</option>
              <option value="CLOSE">CLOSE — candle closes beyond level</option>
            </select>
          </Field>

          {form.breakType === "CLOSE" && (
            <Field label="Timeframe *">
              <select style={selectStyle} value={form.breakTimeframe} onChange={e => s("breakTimeframe", e.target.value)}>
                <option value="">Select timeframe...</option>
                <option value="M1">M1</option>
                <option value="M5">M5</option>
                <option value="M15">M15</option>
                <option value="M30">M30</option>
                <option value="H1">H1</option>
                <option value="H4">H4</option>
                <option value="D1">D1</option>
                <option value="W1">W1</option>
                <option value="MN">Monthly</option>
              </select>
            </Field>
          )}

          <Field label="Entry Price *">
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {form.entries.map((val, idx) => (
                <div key={idx} style={{ display:"flex", gap:8 }}>
                  <input
                    style={inputStyle}
                    type="number" step="any" placeholder={`Entry ${idx+1}`}
                    value={val} onChange={e => setEntryAt(idx, e.target.value)}
                  />
                  {form.entries.length > 1 && (
                    <button onClick={() => removeEntry(idx)} style={{
                      padding:"0 12px", borderRadius:"var(--radius-md)", border:"1px solid var(--border-subtle)",
                      background:"transparent", color:"var(--text-muted)", cursor:"pointer", fontSize:16,
                    }}>×</button>
                  )}
                </div>
              ))}
              {form.entries.length < 5 && (
                <button onClick={addEntry} style={{
                  padding:"8px", borderRadius:"var(--radius-md)", border:"1px dashed var(--border-subtle)",
                  background:"transparent", color:"var(--accent)", cursor:"pointer", fontSize:12, fontWeight:700,
                }}>+ Add Entry Price</button>
              )}
            </div>
          </Field>
        </>
      )}

      {/* SL & TP */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
        <Field label="Stop Loss *">
          <input style={{...inputStyle, borderColor:"rgba(255,92,122,0.4)"}}
            type="number" step="any" placeholder="SL"
            value={form.sl} onChange={e => s("sl", e.target.value)}/>
        </Field>
        <Field label="Take Profit *">
          <input style={{...inputStyle, borderColor:"rgba(29,209,161,0.4)"}}
            type="number" step="any" placeholder="TP (or use R:R below)"
            value={form.tp} onChange={e => s("tp", e.target.value)}/>
        </Field>
      </div>

      {/* R:R input — auto-fill TP */}
      <Field label="R:R Ratio (auto-fill TP)">
        <div style={{ position:"relative" }}>
          <div style={{
            position:"absolute", left:12, top:"50%", transform:"translateY(-50%)",
            fontSize:12, fontWeight:700, color:"var(--text-muted)", pointerEvents:"none",
          }}>1 :</div>
          <input
            style={{...inputStyle, paddingLeft:36, borderColor: form.rrInput ? "rgba(29,209,161,0.5)" : "var(--border-subtle)"}}
            type="number" step="0.1" min="0.1" placeholder="e.g. 1.5"
            value={form.rrInput||""}
            onChange={e => {
              const rrVal = e.target.value;
              s("rrInput", rrVal);
              const en = parseFloat(referenceEntry);
              const sl = parseFloat(form.sl);
              const rrNum = parseFloat(rrVal);
              if (en && sl && rrNum > 0) {
                const risk = form.direction === "BUY" ? en - sl : sl - en;
                if (risk > 0) {
                  const reward = risk * rrNum;
                  const tp = form.direction === "BUY" ? en + reward : en - reward;
                  const refStr = referenceEntry.toString();
                  const slStr = form.sl.toString();
                  const dec = Math.max(
                    refStr.includes(".") ? refStr.split(".")[1].length : 0,
                    slStr.includes(".") ? slStr.split(".")[1].length : 0
                  );
                  s("tp", tp.toFixed(dec));
                }
              }
            }}
          />
        </div>
      </Field>

      {/* R:R indicator */}
      {rr && (
        <div style={{
          marginBottom:16, padding:"10px 14px", borderRadius:"var(--radius-md)",
          background: rrOk ? "rgba(29,209,161,0.1)" : "rgba(255,92,122,0.1)",
          border: `1px solid ${rrOk ? "rgba(29,209,161,0.3)" : "rgba(255,92,122,0.3)"}`,
        }}>
          <span style={{ fontSize:14, fontWeight:900, color: rrOk ? "var(--success)" : "var(--danger)" }}>
            R:R = 1:{rr} {rrOk ? " Good" : ` Min ${rrFloor}:1`}
          </span>
        </div>
      )}

      {/* Notes */}
      <Field label="Notes (optional)">
        <textarea
          style={{...inputStyle, height:60, resize:"vertical"}}
          placeholder="e.g. Break of structure H1, confluence with FVG confluence..."
          value={form.notes} onChange={e => s("notes", e.target.value)}
        />
      </Field>

      {/* Submit */}
      <button
        onClick={submit}
        disabled={loading || sent}
        style={{
          ...btnPrimary,
          width:"100%",
          padding:"13px",
          fontSize:14,
          opacity: loading||sent ? 0.7 : 1,
          marginTop:"auto",
        }}
      >
        {loading ? "Mengirim..." : sent ? "   Terkirim!" : " Send Signal"}
      </button>
    </div>

    <div style={{ display:"flex", flexDirection:"column", gap:12, minWidth:0 }}>
    <div style={{
      padding:"14px 16px",
      background:"var(--bg-surface)", border:"1px solid var(--border)",
      borderRadius:"var(--radius-lg)", display:"flex", flexDirection:"column",
      alignItems:"flex-start", gap:6,
    }}>
      <div style={{ fontSize:15, fontWeight:600, color:"var(--text-main)" }}>Telegram & Discord Notifications</div>
      <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.6 }}>
        Manage your notification channels from Profile & Settings.
      </div>
      <Link to="/profile" style={{
        fontSize:13, fontWeight:700, color:"var(--accent)", textDecoration:"none",
        marginTop:8,
      }}>Configure notifications &rarr;</Link>
    </div>
    <div style={{
      padding:"14px 16px",
      background:"var(--bg-surface)",
      border:"1px solid var(--border)",
      borderRadius:"var(--radius-lg)",
    }}>
      {mode === "PENDING" ? (
        <>
          <div style={{ fontSize:15, fontWeight:600, color:"var(--text-main)", marginBottom:12 }}>How Direct Signal works</div>
          <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.6, marginBottom:16 }}>
            A standard pending order — you set the exact price you want to enter at.
          </div>
          <ol style={{ margin:0, paddingLeft:18, display:"flex", flexDirection:"column", gap:10 }}>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Set your <b style={{color:"var(--text-main)"}}>Entry Price</b>, Stop Loss, and Take Profit.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Signal stays <b style={{color:"var(--text-main)"}}>PENDING</b> until price reaches your Entry.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Once touched, signal becomes <b style={{color:"var(--text-main)"}}>RUNNING</b>.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Closes automatically at TP or SL.</li>
          </ol>
          <div style={{ marginTop:18, padding:"10px 12px", borderRadius:"var(--radius-md)", background:"var(--accent-soft)", border:"1px solid var(--accent-border)", fontSize:12, color:"var(--text-muted)" }}>
            Use this when you already know exactly where you want to enter.
          </div>
        </>
      ) : (
        <>
          <div style={{ fontSize:15, fontWeight:600, color:"var(--text-main)", marginBottom:12 }}>How Break & Retrace Signal works</div>
          <div style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.6, marginBottom:16 }}>
            A two-stage signal — wait for a breakout to confirm, then enter on the retrace. No need to watch the chart yourself.
          </div>
          <ol style={{ margin:0, paddingLeft:18, display:"flex", flexDirection:"column", gap:10 }}>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Set a <b style={{color:"var(--text-main)"}}>Break Price</b> — the level that confirms structure break.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Choose <b style={{color:"var(--text-main)"}}>WICK</b> (price touches) or <b style={{color:"var(--text-main)"}}>CLOSE</b> (candle on your chosen timeframe closes beyond).</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Once broken, signal becomes <b style={{color:"var(--text-main)"}}>ARMED</b> — waiting for price to retrace back to your Entry Price.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Once retraced, signal becomes <b style={{color:"var(--text-main)"}}>RUNNING</b>. Closes automatically at TP or SL.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>Add up to <b style={{color:"var(--text-main)"}}>5 Entry Prices</b> to create several independent signals from one break level — each triggers on its own.</li>
            <li style={{ fontSize:13, color:"var(--text-muted)", lineHeight:1.5 }}>If price closes beyond your <b style={{color:"var(--text-main)"}}>Stop Loss</b> before the break ever happens, the signal auto-cancels — the setup is considered invalid since price already formed a new low/high against your thesis.</li>
          </ol>
          <div style={{ marginTop:18, padding:"10px 12px", borderRadius:"var(--radius-md)", background:"var(--accent-soft)", border:"1px solid var(--accent-border)", fontSize:12, color:"var(--text-muted)" }}>
            Use this when you're waiting for a breakout/BOS confirmation before committing to an entry.
          </div>
        </>
      )}
    </div>
    </div>
    </div>
  );
}

// ── AlphaRank Section ────────────────────────────────────────────────────────
function AlphaRankSection({ data, hasSignalSets, onSendSig }) {
  if(!data) return(
    <div style={{ padding:"48px 24px", textAlign:"center", color:"var(--text-muted)" }}>
      <div style={{ fontSize:32, marginBottom:12 }}></div>
      <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>No performance data yet</div>
      <div style={{ fontSize:12 }}>AlphaRank score will appear once you have closed signals</div>
    </div>
  );
  const gradeColor = GRADE_COLOR[data.grade] || "var(--text-muted)";
  const pillars = data.pillars || [];
  return(
    <div style={{ padding:"24px" }}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:24, marginBottom:28, alignItems:"center" }}>
        <div style={{ textAlign:"center", padding:"24px 32px", background:"var(--bg-elevated-soft)", borderRadius:"var(--radius-lg)", border:`2px solid ${data.closedSignals>=20?gradeColor:"var(--border-subtle)"}`, minWidth:160 }}>
          {data.closedSignals >= 20 ? (
            <>
              <div style={{ fontSize:11, fontWeight:800, color:"var(--text-muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>AlphaScore™</div>
              <div style={{ fontSize:52, fontWeight:900, color:gradeColor, lineHeight:1 }}>{data.alphaScore>0?data.alphaScore.toFixed(1):"—"}</div>
              <div style={{ fontSize:28, fontWeight:900, color:gradeColor, marginTop:4 }}>{data.grade}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize:11, fontWeight:800, color:"var(--text-muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:8 }}>AlphaScore™</div>
              <div style={{ fontSize:32, fontWeight:900, color:"var(--text-muted)", lineHeight:1.2, marginBottom:8 }}>—</div>
              <div style={{ fontSize:12, color:"var(--text-muted)", lineHeight:1.5 }}>Available after<br/><span style={{color:"var(--accent)",fontWeight:700}}>20 closed signals</span><br/><span style={{fontSize:11}}>({data.closedSignals}/20 so far)</span></div>
            </>
          )}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))", gap:10 }}>
          {[
            {label:"Win Rate", value:fmtPct(data.winRate||0), good:null},
            {label:"Avg R:R",  value:data.avgRR>0?("1:"+fmt2(data.avgRR)):"—", good:data.avgRR>=1.5},
            {label:"Profit Factor", value:data.profitFactor>0?fmt2(data.profitFactor):"—", good:null},
            {label:"Closed Signals", value:data.closedSignals, good:null},
            {label:"Wins", value:data.wins, good:data.wins>0},
            {label:"Losses", value:data.losses, good:data.losses===0},
          ].map(({label,value,good})=>(
            <div key={label} style={{ padding:"14px 12px", background:"var(--bg-elevated-soft)", borderRadius:"var(--radius-md)", textAlign:"center" }}>
              <div style={{ fontSize:10, fontWeight:700, color:"var(--text-muted)", letterSpacing:0.8, textTransform:"uppercase", marginBottom:6 }}>{label}</div>
              <div style={{ fontSize:18, fontWeight:900, color:good===null?"var(--text-main)":good?"var(--success)":"var(--danger)" }}>{value}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ fontSize:11, fontWeight:800, color:"var(--text-muted)", letterSpacing:1, textTransform:"uppercase", marginBottom:14 }}>AlphaRank™ Pillars</div>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))", gap:12 }}>
        {pillars.map(p=>{
          const pct = p.score;
          const col = pct>=80?"var(--success)":pct>=60?"var(--accent)":pct>=40?"var(--warning)":"var(--danger)";
          return(
            <div key={p.code} style={{ padding:"16px", background:"var(--bg-elevated-soft)", borderRadius:"var(--radius-md)", border:"1px solid var(--border-subtle)" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <div>
                  <span style={{ fontSize:10, fontWeight:800, color:"var(--text-muted)", letterSpacing:0.8 }}>{p.code}</span>
                  <div style={{ fontSize:13, fontWeight:800, color:"var(--text-main)", marginTop:2 }}>{p.name}</div>
                </div>
                <div style={{ fontSize:22, fontWeight:900, color:col }}>{pct.toFixed(0)}</div>
              </div>
              <div style={{ height:4, background:"var(--border-subtle)", borderRadius:2, marginBottom:8 }}>
                <div style={{ height:"100%", width:`${pct}%`, background:col, borderRadius:2, transition:"width 0.6s ease" }}/>
              </div>
              <div style={{ fontSize:10, color:"var(--text-muted)" }}>{p.reason}</div>
            </div>
          );
        })}
      </div>
      {data.flags && data.flags.length > 0 && (
        <div style={{ marginTop:16 }}>
          <div style={{ fontSize:11, fontWeight:800, color:"var(--danger)", letterSpacing:0.8, textTransform:"uppercase", marginBottom:10 }}>Risk Flags ({data.flags.length})</div>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {data.flags.map(f => (
              <div key={f.code} style={{ padding:"12px 16px", borderRadius:"var(--radius-md)", background: f.severity==="MAJOR"?"rgba(255,92,122,0.08)":"rgba(254,202,87,0.08)", border: `1px solid ${f.severity==="MAJOR"?"rgba(255,92,122,0.3)":"rgba(254,202,87,0.3)"}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <span style={{ fontSize:11, fontWeight:800, color:f.severity==="MAJOR"?"var(--danger)":"var(--warning)" }}>{f.severity}</span>
                  <span style={{ fontSize:12, fontWeight:700, color:"var(--text-main)", marginLeft:10 }}>{f.title}</span>
                  <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:3 }}>{f.desc}</div>
                </div>
                <div style={{ fontSize:12, fontWeight:800, color:"var(--danger)", whiteSpace:"nowrap", marginLeft:16 }}>-{f.penalty} pts</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GrowthChart({ signals }) {
  const [mode, setMode] = React.useState("rr");
  const closed = signals.filter(s => s.status === "CLOSED_TP" || s.status === "CLOSED_SL").slice().reverse();
  if (closed.length === 0) return (
    <div style={{ height:160, display:"flex", alignItems:"center", justifyContent:"center", color:"var(--text-muted)", fontSize:12 }}>No closed signals yet</div>
  );
  const getPips = (s) => {
    if(s.pips!=null) return s.pips;
    const e=parseFloat(s.entry), sl=parseFloat(s.sl), tp=parseFloat(s.tp);
    const m=pipMult(s.pair||"");
    if(!e||!sl||!tp) return 0;
    if(s.status==="CLOSED_TP") return Math.abs(tp-e)*m;
    return -Math.abs(e-sl)*m;
  };
  const calcRR = (s) => {
    const e=parseFloat(s.entry), sl=parseFloat(s.sl), tp=parseFloat(s.tp);
    const risk=Math.abs(e-sl), reward=Math.abs(tp-e);
    if(risk<=0) return 0;
    return Math.round(reward/risk*100)/100;
  };
  let cum = 0;
  const points = [{x:0,y:0}];
  closed.forEach((s,i) => {
    if(mode==="rr") cum += s.status==="CLOSED_TP" ? calcRR(s) : -1;
    else cum += getPips(s);
    points.push({x:i+1,y:Math.round(cum*100)/100});
  });
  const maxY=Math.max(...points.map(p=>p.y),0.1), minY=Math.min(...points.map(p=>p.y),0), rangeY=maxY-minY||1;
  const W=600,H=140,PAD=20;
  const toX=(i)=>PAD+(i/(points.length-1||1))*(W-PAD*2);
  const toY=(v)=>PAD+((maxY-v)/rangeY)*(H-PAD*2);
  const pathD=points.map((p,i)=>`${i===0?"M":"L"}${toX(i).toFixed(1)},${toY(p.y).toFixed(1)}`).join(" ");
  const areaD=pathD+` L${toX(points.length-1).toFixed(1)},${toY(minY).toFixed(1)} L${toX(0).toFixed(1)},${toY(minY).toFixed(1)} Z`;
  const lastY=points[points.length-1].y;
  const col=lastY>=0?"var(--success)":"var(--danger)";
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:160}} preserveAspectRatio="none">
        <defs><linearGradient id="gg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={lastY>=0?"var(--success)":"var(--danger)"} stopOpacity="0.3"/>
          <stop offset="100%" stopColor={lastY>=0?"var(--success)":"var(--danger)"} stopOpacity="0"/>
        </linearGradient></defs>
        <line x1={PAD} y1={toY(0).toFixed(1)} x2={W-PAD} y2={toY(0).toFixed(1)} stroke="var(--border-subtle)" strokeWidth="1" strokeDasharray="4,4"/>
        <path d={areaD} fill="url(#gg)"/>
        <path d={pathD} fill="none" stroke={col} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <circle cx={toX(points.length-1)} cy={toY(lastY)} r="4" fill={col}/>
      </svg>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
        <span style={{fontSize:10,color:"var(--text-muted)"}}>Start</span>
        <div style={{display:"flex",gap:4}}>
          {["rr","pips"].map(m=>(
            <button key={m} onClick={()=>setMode(m)} style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:4,cursor:"pointer",border:"1px solid var(--border-subtle)",background:mode===m?"var(--accent)":"var(--bg-elevated)",color:mode===m?"#fff":"var(--text-muted)"}}>
              {m==="rr"?"R:R":"Pips"}
            </button>
          ))}
        </div>
        <span style={{fontSize:12,fontWeight:800,color:col}}>{lastY>=0?"+":""}{lastY.toFixed(2)} {mode==="rr"?"R":"pts"}</span>
        <span style={{fontSize:10,color:"var(--text-muted)"}}>Latest</span>
      </div>
    </div>
  );
}

function FrequencyBars({ pending, running, wins, losses, cancelled, total }) {
  void pending;
  const settled=wins+losses;
  const bars=[
    {label:"Running/Active", val:running, max:Math.max(total,1), color:"var(--warning)"},
    {label:"Profit/Settled", val:wins,    max:Math.max(settled,1), color:"var(--success)"},
    {label:"Loss/Settled",   val:losses,  max:Math.max(settled,1), color:"var(--danger)"},
    {label:"Cancelled",      val:cancelled, max:Math.max(total,1), color:"var(--text-main)"},
    {label:"Total Posted",   val:total,   max:Math.max(total,1),   color:"var(--accent)"},
  ];
  return (
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {bars.map(({label,val,max,color})=>(
        <div key={label}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:6}}>
            <span style={{fontSize:11,color:"var(--text-muted)"}}>{label}</span>
            <span style={{fontSize:12,fontWeight:800,color:"var(--text-main)"}}>{val}/{max}</span>
          </div>
          <div style={{height:6,background:"var(--border-subtle)",borderRadius:3}}>
            <div style={{height:"100%",width:`${Math.min(100,val/max*100)}%`,background:color,borderRadius:3,transition:"width 0.6s ease"}}/>
          </div>
        </div>
      ))}
    </div>
  );
}

function SummaryStats({ alphaRank, signals }) {
  const closed=signals.filter(s=>s.status==="CLOSED_TP"||s.status==="CLOSED_SL");
  const allDates=signals.map(s=>s.issuedAt).filter(Boolean);
  let avgPerMonth=0, avgPerWeek=0;
  if(allDates.length>=2){
    const first=new Date(allDates[allDates.length-1]), last=new Date(allDates[0]);
    const months=Math.max(1,(last-first)/(1000*60*60*24*30));
    const weeks=Math.max(1,(last-first)/(1000*60*60*24*7));
    avgPerMonth=Math.round(signals.length/months*10)/10;
    avgPerWeek=Math.round(signals.length/weeks*10)/10;
  }
  const cumRR = alphaRank?.cumulativeR ?? 0;
  const tpPips=[], slPips=[];
  closed.forEach(s=>{
    const e=parseFloat(s.entry), sl=parseFloat(s.sl), tp=parseFloat(s.tp);
    const m=pipMult(s.pair||"");
    if(e&&sl&&tp){ tpPips.push(Math.abs(tp-e)*m); slPips.push(Math.abs(e-sl)*m); }
  });
  const avgTP=tpPips.length?Math.round(tpPips.reduce((a,b)=>a+b,0)/tpPips.length*100)/100:0;
  const avgSL=slPips.length?Math.round(slPips.reduce((a,b)=>a+b,0)/slPips.length*100)/100:0;
  const maxTP=tpPips.length?Math.round(Math.max(...tpPips)*100)/100:0;
  const maxSL=slPips.length?Math.round(Math.max(...slPips)*100)/100:0;
  let maxConsecWin=0, curW=0;
  signals.slice().reverse().forEach(s=>{
    if(s.status==="CLOSED_TP"){curW++;if(curW>maxConsecWin)maxConsecWin=curW;}
    else if(s.status==="CLOSED_SL"){curW=0;}
  });
  const netPips = (alphaRank?.totalPipsWin??0)-(alphaRank?.totalPipsLoss??0);
  const StatBox=({label,value,color,big})=>(
    <div style={{padding:"14px",background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",border:"1px solid var(--border-subtle)"}}>
      <div style={{fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:0.8,textTransform:"uppercase",marginBottom:8}}>{label}</div>
      <div style={{fontSize:big?22:18,fontWeight:900,color}}>{value}</div>
    </div>
  );
  const rows=[
    [{label:"Cumulative R",value:cumRR>=0?`+${cumRR.toFixed(2)}`:`${cumRR.toFixed(2)}`,color:cumRR>=0?"var(--success)":"var(--danger)",big:true},{label:"Average R:R",value:alphaRank?.avgRR>0?`1:${alphaRank.avgRR.toFixed(2)}`:"—",color:"var(--text-main)",big:true},{label:"Avg Signal/Month",value:avgPerMonth>0?`${avgPerMonth}`:"—",color:"var(--text-main)",big:true},{label:"Avg Signal/Week",value:avgPerWeek>0?`${avgPerWeek}`:"—",color:"var(--text-main)",big:true}],
    [{label:"Avg TP (pts)",value:avgTP>0?fmtPts(avgTP):"—",color:"var(--success)"},{label:"Avg SL (pts)",value:avgSL>0?fmtPts(avgSL):"—",color:"var(--danger)"},{label:"Max TP (pts)",value:maxTP>0?fmtPts(maxTP):"—",color:"var(--success)"},{label:"Max SL (pts)",value:maxSL>0?fmtPts(maxSL):"—",color:"var(--danger)"}],
    [{label:"Win Rate",value:alphaRank?.winRate>0?`${alphaRank.winRate.toFixed(1)}%`:"—",color:"var(--text-main)"},{label:"Profit Factor",value:alphaRank?.profitFactor>0?alphaRank.profitFactor.toFixed(2):"—",color:"var(--text-main)"},{label:"Consec. Wins",value:maxConsecWin,color:"var(--success)"},{label:"Consec. Losses",value:alphaRank?.maxConsecLoss??0,color:"var(--danger)"}],
    [{label:"Total Pips Win",value:alphaRank?.totalPipsWin>0?"+"+fmtPts(alphaRank.totalPipsWin):"—",color:"var(--success)"},{label:"Total Pips Loss",value:alphaRank?.totalPipsLoss>0?"-"+fmtPts(alphaRank.totalPipsLoss):"—",color:"var(--danger)"},{label:"Net Pips",value:netPips!==0?(netPips>0?"+":"")+fmtPts(Math.abs(netPips)):"—",color:netPips>=0?"var(--success)":"var(--danger)"},{label:"PF (Pips basis)",value:alphaRank?.profitFactor>0?alphaRank.profitFactor.toFixed(2):"—",color:"var(--text-main)"}],
  ];
  return (
    <div style={{background:"var(--bg-elevated-soft)",borderRadius:"var(--radius-md)",border:"1px solid var(--border-subtle)",padding:"20px"}}>
      <div style={{fontSize:12,fontWeight:800,color:"var(--text-main)",marginBottom:14}}>Summary Channel</div>
      {rows.map((row,i)=>(
        <div key={i} style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:10}}>
          {row.map(p=><StatBox key={p.label} {...p}/>)}
        </div>
      ))}
    </div>
  );
}


// ── Settings Tab ─────────────────────────────────────────────────────────────
function SettingsTab({ signalSet, onSaved }) {
  const [form, setForm] = useState({
    name:        signalSet?.name        || "",
    description: signalSet?.description || "",
    market:      signalSet?.market      || "",
    style:       signalSet?.style       || "",
  });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg]         = useState("");
  const [err, setErr]         = useState("");

  const inp = {
    width:"100%", background:"var(--bg-elevated-soft)",
    border:"1px solid var(--border-subtle)", borderRadius:"var(--radius-md)",
    padding:"10px 14px", fontSize:13, color:"var(--text-main)",
    outline:"none", boxSizing:"border-box",
  };

  const save = async () => {
    if (!form.name.trim()) { setErr("Plan name is required"); return; }
    setLoading(true); setErr(""); setMsg("");
    try {
      await api.updateAnalystSignalSet(signalSet.id, {
        name:        form.name.trim(),
        description: form.description.trim(),
        market:      form.market.trim(),
        style:       form.style.trim(),
      });
      setMsg(" Saved"); onSaved();
    } catch(e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ padding:"28px 24px", maxWidth:560 }}>
      <div style={{ fontSize:15, fontWeight:800, color:"var(--text-main)", marginBottom:4 }}>
        Plan Profile
      </div>
      <div style={{
        fontSize:12, color:"var(--text-muted)", marginBottom:24,
        borderLeft:"2px solid var(--accent)", paddingLeft:10, fontStyle:"italic",
      }}>
        This is how your plan appears in the Strategy Marketplace.
      </div>

      {[
        ["Plan Name *",     "name",        "text",     "e.g. Gold Precision Plan"],
        ["Market Focus",    "market",      "text",     "e.g. XAUUSD, Forex Majors"],
        ["Trading Style",   "style",       "text",     "e.g. Swing, Scalping, Position"],
        ["About This Plan", "description", "textarea", "Describe your edge, approach, and what investors can expect..."],
      ].map(([label, key, type, ph]) => (
        <div key={key} style={{ marginBottom:16 }}>
          <div style={{
            fontSize:10, fontWeight:800, color:"var(--text-muted)",
            letterSpacing:0.8, textTransform:"uppercase", marginBottom:6,
          }}>{label}</div>
          {type === "textarea"
            ? <textarea rows={4} placeholder={ph} value={form[key]}
                onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                style={{...inp, resize:"vertical"}} />
            : <input type="text" placeholder={ph} value={form[key]}
                onChange={e => setForm(f => ({...f, [key]: e.target.value}))}
                style={inp} />
          }
        </div>
      ))}

      {err && <div style={{ fontSize:12, color:"var(--danger)",  marginBottom:12 }}>{err}</div>}
      {msg && <div style={{ fontSize:12, color:"var(--success)", marginBottom:12 }}>{msg}</div>}

      <button onClick={save} disabled={loading} style={{
        padding:"10px 24px", borderRadius:"var(--radius-md)",
        border:"none", background:"var(--accent)", color:"#fff",
        fontSize:13, fontWeight:700, cursor:"pointer",
        opacity: loading ? 0.6 : 1,
      }}>
        {loading ? "Saving..." : "Save Plan Profile"}
      </button>
    </div>
  );
}



function calcVirtualAlphaScore(signals, vResult) {
  const closed = (signals||[])
    .filter(s => s.status==="CLOSED_TP"||s.status==="CLOSED_SL")
    .slice().sort((a,b)=>new Date(a.issuedAt||a.closedAt||0)-new Date(b.issuedAt||b.closedAt||0));
  if(closed.length < 10) return null;

  const n=closed.length, wins=closed.filter(s=>s.status==="CLOSED_TP").length;
  const winRate=wins/n*100;

  // R:R tiap signal
  const rrList=closed.map(s=>{
    const e=parseFloat(s.entry),sl=parseFloat(s.sl),tp=parseFloat(s.tp);
    if(!e||!sl||!tp) return 1;
    const rd=Math.abs(e-sl),rwd=Math.abs(tp-e);
    return rd>0?rwd/rd:1;
  });
  const avgRR=rrList.reduce((a,b)=>a+b,0)/n;
  const rrCompliance=rrList.filter(r=>r>=1.0).length/n*100;

  // Monthly consistency dari virtual curve
  const curve=vResult.curve;
  const monthMap={};
  closed.forEach((s,i)=>{
    const d=new Date(s.closedAt||s.issuedAt||0);
    const k=`${d.getFullYear()}-${d.getMonth()}`;
    if(!monthMap[k]) monthMap[k]=0;
    const prev=curve[i]?.v||VIRTUAL_INITIAL, curr=curve[i+1]?.v||VIRTUAL_INITIAL;
    monthMap[k]+=curr-prev;
  });
  const months=Object.values(monthMap);
  const profitMonths=months.filter(m=>m>0).length;
  const monthConsistency=months.length>0?profitMonths/months.length*100:50;

  // Max consecutive losses
  let maxCL=0,curL=0;
  closed.forEach(s=>{ if(s.status==="CLOSED_SL"){curL++;if(curL>maxCL)maxCL=curL;}else curL=0; });

  // Track record
  const firstD=new Date(closed[0].issuedAt||closed[0].closedAt||0);
  const lastD=new Date(closed[n-1].closedAt||closed[n-1].issuedAt||0);
  const daysActive=Math.max(1,Math.round((lastD-firstD)/(86400000)));
  const sigPerMonth=n/Math.max(1,daysActive/30);

  const {roi,maxDD}=vResult;

  // P1 Profitability (20%) — virtual ROI
  const p1=roi>50?100:roi>20?80+(roi-20)/30*20:roi>10?60+(roi-10)/10*20:roi>0?40+roi/10*20:Math.max(0,40+roi*1.5);

  // P2 Consistency (13%) — monthly + consec loss
  const p2=Math.min(100, monthConsistency*0.7+Math.max(0,(5-maxCL)/5*30));

  // P3 Risk Management (12%) — analyst WAJIB SL/TP, score dari R:R compliance + low DD
  const p3=Math.min(100, rrCompliance*0.6+Math.max(0,(20-maxDD)/20*40));

  // P4 Recovery Resilience (10%) — inverse max DD
  const p4=maxDD<5?100:maxDD<10?90:maxDD<15?75:maxDD<20?60:maxDD<30?40:maxDD<50?20:0;

  // P5 Trading Edge (13%) — win rate + avg R:R
  const p5=Math.min(100, winRate*0.5+Math.min(avgRR/2.5,1)*50);

  // P6 Discipline (12%) — R:R compliance + consistency R:R
  const rrVar=rrList.reduce((a,r)=>a+Math.pow(r-avgRR,2),0)/n;
  const rrStd=Math.sqrt(rrVar);
  const p6=Math.min(100, rrCompliance*0.6+Math.max(0,(1-Math.min(rrStd/2,1))*40));

  // P7 Track Record (20%) — volume + duration + activity
  const p7=Math.min(100, Math.min(n/100,1)*40+Math.min(daysActive/180,1)*35+Math.min(sigPerMonth/8,1)*25);

  const raw=p1*0.20+p2*0.13+p3*0.12+p4*0.10+p5*0.13+p6*0.12+p7*0.20;
  const score=Math.round(Math.min(raw,100)*10)/10;
  const grade=score>=90?"A+":score>=85?"A":score>=80?"A-":score>=75?"B+":score>=70?"B":score>=65?"B-":score>=60?"C+":score>=55?"C":score>=50?"C-":score>=40?"D":"F";

  // Virtual flags
  const vFlags=[];
  if(maxDD>20) vFlags.push({severity:"MAJOR",title:"High Virtual Drawdown",desc:`Virtual account drawdown reached ${maxDD.toFixed(1)}% — signals caused significant equity drop`});
  if(winRate<40) vFlags.push({severity:"MAJOR",title:"Low Win Rate",desc:`Only ${winRate.toFixed(1)}% of signals hit TP — profitability at risk`});
  if(avgRR<1.0) vFlags.push({severity:"MINOR",title:"Low Average R:R",desc:`Avg R:R is 1:${avgRR.toFixed(2)} — risking more than reward on average`});
  if(maxCL>=5) vFlags.push({severity:"MINOR",title:"Consecutive Losses",desc:`Max ${maxCL} consecutive losing signals — consistency concern`});

  return {
    score, grade,
    pillars:[
      {code:"P1",name:"Profitability",      score:Math.round(p1), reason:`Virtual ROI: ${roi>=0?"+":""}${roi.toFixed(1)}% on $1,000 starting balance`},
      {code:"P2",name:"Consistency",        score:Math.round(p2), reason:`${profitMonths}/${months.length} profitable months, max ${maxCL} consec. losses`},
      {code:"P3",name:"Risk Management",    score:Math.round(p3), reason:`${rrCompliance.toFixed(0)}% signals R:R≥1:1 · all signals have SL/TP`},
      {code:"P4",name:"Recovery Resilience",score:Math.round(p4), reason:`Max virtual drawdown: ${maxDD.toFixed(1)}%`},
      {code:"P5",name:"Trading Edge",       score:Math.round(p5), reason:`Win rate ${winRate.toFixed(1)}%, avg R:R 1:${avgRR.toFixed(2)}`},
      {code:"P6",name:"Discipline",         score:Math.round(p6), reason:`R:R compliance ${rrCompliance.toFixed(0)}%, std deviation ${rrStd.toFixed(2)}`},
      {code:"P7",name:"Track Record",       score:Math.round(p7), reason:`${n} signals · ${daysActive} days · ${sigPerMonth.toFixed(1)}/month`},
    ],
    vFlags,
    winRate, avgRR, rrCompliance, profitMonths, totalMonths:months.length, daysActive,
  };
}
// ── Virtual Equity Chart ─────────────────────────────────────────────────────
function VirtualEquityChart({ curve, netPnL, roi, riskPct:chartRiskPct }) {
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  if (!curve || curve.length < 2) return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:32,textAlign:"center",color:"var(--text-faint)",marginBottom:16}}>
      <div style={{fontSize:32,marginBottom:8}}></div>
      <div style={{fontSize:13}}>No closed signals yet — virtual equity curve will appear here</div>
    </div>
  );
  const W=900,H=220,PAD={top:16,right:16,bottom:32,left:68};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const vals=curve.map(p=>p.v);
  const minV=Math.min(...vals)*0.994, maxV=Math.max(...vals)*1.006;
  const xOf=i=>PAD.left+(i/Math.max(curve.length-1,1))*innerW;
  const yOf=v=>PAD.top+innerH-((v-minV)/(maxV-minV||1))*innerH;
  const pathD=curve.map((p,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(" ");
  const areaD=pathD+` L${xOf(curve.length-1).toFixed(1)},${yOf(minV).toFixed(1)} L${xOf(0).toFixed(1)},${yOf(minV).toFixed(1)} Z`;
  const col=netPnL>=0?"var(--accent)":"var(--danger)";
  const growthColor=roi>=0?"var(--success)":"var(--danger)";
  const yTicks=Array.from({length:5},(_,i)=>minV+(maxV-minV)*(i/4));
  const peakVal=Math.max(...vals);
  const baseY=yOf(VIRTUAL_INITIAL);
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16,overflow:"hidden"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Equity Growth Curve</div>
          <div style={{fontSize:10,color:"var(--text-faint)"}}>$1,000 start · {chartRiskPct||1.5}% risk/trade (compounding)</div>
          <span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",
            background:roi>=0?"var(--success-soft)":"var(--danger-soft)",color:growthColor}}>
            {roi>=0?"+":""}{fmt2(roi)}%
          </span>
        </div>
      </div>
      <div style={{position:"relative",width:"100%",aspectRatio:`${W}/${H}`,minHeight:"150px",maxHeight:"300px"}}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",display:"block"}}
          onMouseMove={e=>{
            const rect=svgRef.current?.getBoundingClientRect();
            if(!rect)return;
            const x=(e.clientX-rect.left)/rect.width*W;
            const idx=Math.min(curve.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(curve.length-1))));
            setHover({idx,x:xOf(idx),point:curve[idx]});
          }}
          onMouseLeave={()=>setHover(null)}>
          <defs>
            <linearGradient id="va-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={netPnL>=0?"#4e9af1":"var(--danger)"} stopOpacity="0.28"/>
              <stop offset="100%" stopColor={netPnL>=0?"#4e9af1":"var(--danger)"} stopOpacity="0.02"/>
            </linearGradient>
          </defs>
          {yTicks.map((v,i)=>(
            <g key={i}>
              <line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/>
              <text x={PAD.left-6} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">${fmt2(v)}</text>
            </g>
          ))}
          <line x1={PAD.left} y1={baseY} x2={W-PAD.right} y2={baseY}
            stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="5 3"/>
          <path d={areaD} fill="url(#va-fill)"/>
          <path d={pathD} fill="none" stroke={col} strokeWidth="2"/>
          {hover&&(<>
            <line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke={col} strokeWidth="0.8" opacity="0.5"/>
            <circle cx={hover.x} cy={yOf(hover.point.v)} r="4" fill={col} stroke="var(--bg-surface)" strokeWidth="2"/>
          </>)}
        </svg>
        {hover&&hover.point&&(
          <div style={{position:"absolute",top:8,
            left:hover.idx>curve.length*0.6?"auto":(hover.x/W*100)+"%",
            right:hover.idx>curve.length*0.6?(100-hover.x/W*100)+"%":"auto",
            background:"var(--bg-elevated)",border:"1px solid var(--border)",
            borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,
            pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
            {hover.point.t&&<div style={{color:"var(--text-faint)",marginBottom:4}}>{new Date(hover.point.t).toLocaleDateString()}</div>}
            <div style={{fontWeight:700,color:col}}>Balance: ${fmt2(hover.point.v)}</div>
            <div style={{color:(hover.point.v-VIRTUAL_INITIAL)>=0?"var(--success)":"var(--danger)",marginTop:2}}>
              P&L: {(hover.point.v-VIRTUAL_INITIAL)>=0?"+$":"-$"}{fmt2(Math.abs(hover.point.v-VIRTUAL_INITIAL))}
            </div>
          </div>
        )}
      </div>
      <div style={{display:"flex",gap:16,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border-subtle)",flexWrap:"wrap"}}>
        {[
          {label:"Start",   value:"$1,000.00"},
          {label:"Current", value:"$"+fmt2(curve[curve.length-1]?.v||1000), color:growthColor},
          {label:"Peak",    value:"$"+fmt2(peakVal)},
          {label:"Net P&L", value:(netPnL>=0?"+$":"-$")+fmt2(Math.abs(netPnL)), color:growthColor},
          {label:"Signals", value:(curve.length-1)+" closed"},
        ].map(s=>(
          <div key={s.label}>
            <div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{s.label}</div>
            <div style={{fontSize:13,fontWeight:700,color:s.color||"var(--text-main)"}}>{s.value}</div>
          </div>
        ))}
        {curve.length>1&&curve[0].t&&(()=>{ const first=new Date(curve[0].t); const last=new Date(curve[curve.length-1].t); const d=Math.round((last-first)/(1000*60*60*24)); const fmt=dt=>`${dt.getDate()}/${dt.getMonth()+1}/${dt.getFullYear()}`; return <div style={{marginLeft:"auto"}}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Trading Period</div><div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)"}}>{fmt(first)} → {fmt(last)} <span style={{color:"var(--accent)"}}>({d} days)</span></div></div>; })()}
      </div>
    </div>
  );
}

// ── Analyst Performance Tab — Virtual Trader View ─────────────────────────────
function VirtualDrawdownChart({ curve }) {
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  const ddSeries = React.useMemo(()=>{
    let peak = VIRTUAL_INITIAL;
    return (curve||[]).map(p=>{
      if(p.v>peak) peak=p.v;
      const dd = peak>0 ? (peak-p.v)/peak*100 : 0;
      return {t:p.t, dd:-dd};
    });
  },[curve]);
  if(ddSeries.length<2) return null;
  const W=900,H=160,PAD={top:12,right:16,bottom:28,left:52};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const minDD=Math.min(...ddSeries.map(d=>d.dd),-1)*1.1;
  const xOf=i=>PAD.left+(i/Math.max(ddSeries.length-1,1))*innerW;
  const yOf=v=>PAD.top+(v/minDD)*innerH;
  const pathD=ddSeries.map((d,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(d.dd).toFixed(1)}`).join(" ");
  const areaD=pathD+` L${xOf(ddSeries.length-1).toFixed(1)},${PAD.top} L${xOf(0).toFixed(1)},${PAD.top} Z`;
  const yTicks=[0,-5,-10,-20,-30].filter(v=>v>=minDD);
  const maxDDv=Math.abs(Math.min(...ddSeries.map(d=>d.dd)));
  const ddColor=maxDDv<=10?"var(--success)":maxDDv<=20?"var(--warning)":"var(--danger)";
  const xTickCount=Math.min(6,ddSeries.length);
  const xTickIdxs=ddSeries.length<=1?[0]:Array.from({length:xTickCount},(_,i)=>Math.round(i*(ddSeries.length-1)/(xTickCount-1)));
  const fmtDate=str=>{ if(!str)return""; const d=new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; };
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Drawdown — Underwater Curve</div>
        <span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:"var(--danger-soft)",color:ddColor}}>Max DD: {maxDDv.toFixed(2)}%</span>
      </div>
      <div style={{position:"relative",width:"100%",aspectRatio:`${W}/${H}`,minHeight:"100px",maxHeight:"200px"}}>
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{position:"absolute",top:0,left:0,width:"100%",height:"100%",display:"block"}}
          onMouseMove={e=>{const rect=svgRef.current?.getBoundingClientRect();if(!rect)return;const x=(e.clientX-rect.left)/rect.width*W;const idx=Math.min(ddSeries.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(ddSeries.length-1))));setHover({idx,x:xOf(idx),point:ddSeries[idx]});}}
          onMouseLeave={()=>setHover(null)}>
          <defs><linearGradient id="vdd-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(239,68,68,0.35)"/><stop offset="100%" stopColor="rgba(239,68,68,0.05)"/></linearGradient></defs>
          {yTicks.map(v=>(
            <g key={v}>
              <line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/>
              <text x={PAD.left-4} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{v}%</text>
            </g>
          ))}
          <line x1={PAD.left} y1={PAD.top} x2={W-PAD.right} y2={PAD.top} stroke="var(--border)" strokeWidth="1"/>
          <path d={areaD} fill="url(#vdd-fill)"/>
          <path d={pathD} fill="none" stroke="var(--danger)" strokeWidth="1.5"/>
          {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtDate(ddSeries[i]?.t)}</text>)}
          {hover&&(<>
            <line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--danger)" strokeWidth="0.8" opacity="0.4"/>
            <circle cx={hover.x} cy={yOf(hover.point.dd)} r="4" fill="var(--danger)" stroke="var(--bg-surface)" strokeWidth="2"/>
          </>)}
        </svg>
        {hover&&hover.point&&(
          <div style={{position:"absolute",top:8,left:hover.idx>ddSeries.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>ddSeries.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
            {hover.point.t&&<div style={{color:"var(--text-faint)",marginBottom:4}}>{new Date(hover.point.t).toLocaleDateString()}</div>}
            <div style={{fontWeight:700,color:"var(--danger)"}}>DD: {Math.abs(hover.point.dd).toFixed(2)}%</div>
          </div>
        )}
      </div>
    </div>
  );
}

function VirtualMonthlyChart({ trades }) {
  const now = new Date();
  const allData = React.useMemo(()=>{
    const map = {};
    (trades||[]).forEach(t=>{
      const d = new Date(t.closeTime);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      if(!map[key]) map[key] = {key, year:d.getFullYear(), profit:0, trades:0, wins:0, label:d.toLocaleString('en-US',{month:'short',year:'2-digit'})};
      map[key].profit += t.pnl; map[key].trades += 1;
      if(t.pnl>0) map[key].wins += 1;
    });
    return Object.values(map).sort((a,b)=>a.key.localeCompare(b.key));
  },[trades]);
  const dataYears = [...new Set(allData.map(m=>m.year))].sort((a,b)=>b-a);
  const [selYear, setSelYear] = React.useState(()=> dataYears[0] || now.getFullYear());
  const data = allData.filter(m=>m.year===selYear);
  if(allData.length===0) return null;
  const maxAbs = Math.max(...data.map(m=>Math.abs(m.profit)),1);
  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:12};
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Monthly Performance</div>
        <select value={selYear} onChange={e=>setSelYear(Number(e.target.value))} style={selStyle}>
          {dataYears.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"flex-end",height:140,overflowX:"auto",paddingBottom:4}}>
        {data.map(m=>{
          const pos = m.profit>=0;
          const pct = Math.max((Math.abs(m.profit)/maxAbs)*100,8);
          return (
            <div key={m.key} style={{flex:"0 0 52px",width:52,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
              <div style={{fontSize:10,fontWeight:600,color:pos?"var(--success)":"var(--danger)"}}>{(pos?"+$":"-$")+fmt2(Math.abs(m.profit))}</div>
              <div style={{width:"75%",height:`${pct}%`,borderRadius:"4px 4px 0 0",background:pos?"var(--success)":"var(--danger)",opacity:0.9,minHeight:8}}/>
              <div style={{fontSize:10,color:"var(--text-faint)"}}>{m.label}</div>
            </div>
          );
        })}
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:10,fontSize:11,color:"var(--text-faint)"}}>
        <span>Months with data: {data.filter(m=>m.profit!==0).length}</span>
        <span>Profitable: {data.filter(m=>m.profit>0).length}</span>
        <span>Loss: {data.filter(m=>m.profit<0).length}</span>
      </div>
    </div>
  );
}

function VirtualWeeklyChart({ trades }) {
  const now = new Date();
  const MONTH_NAMES = {1:"Jan",2:"Feb",3:"Mar",4:"Apr",5:"May",6:"Jun",7:"Jul",8:"Aug",9:"Sep",10:"Oct",11:"Nov",12:"Dec"};
  const allData = React.useMemo(()=>{
    const map = {};
    (trades||[]).forEach(t=>{
      const d = new Date(t.closeTime);
      const onejan = new Date(d.getFullYear(),0,1);
      const week = Math.ceil((((d-onejan)/86400000)+onejan.getDay()+1)/7);
      const key = `${d.getFullYear()}-W${String(week).padStart(2,'0')}`;
      if(!map[key]) map[key] = {key, year:d.getFullYear(), month:d.getMonth()+1, profit:0, trades:0, label:`W${week}`, dateLabel:`${d.getDate()}/${d.getMonth()+1}`};
      map[key].profit += t.pnl; map[key].trades += 1;
    });
    return Object.values(map).sort((a,b)=>a.key.localeCompare(b.key));
  },[trades]);
  const dataYears = [...new Set(allData.map(w=>w.year))].sort((a,b)=>b-a);
  const [selYear, setSelYear] = React.useState(()=> dataYears[0] || now.getFullYear());
  const [selMonth, setSelMonth] = React.useState(()=> now.getMonth()+1);
  const data = allData.filter(w=>w.year===selYear && w.month===selMonth);
  if(allData.length===0) return null;
  const maxAbs = Math.max(...data.map(w=>Math.abs(w.profit)),1);
  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:12};
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-xl)",padding:"20px 24px",marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Weekly Performance</div>
        <div style={{display:"flex",gap:6}}>
          <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))} style={selStyle}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m=><option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
          </select>
          <select value={selYear} onChange={e=>setSelYear(Number(e.target.value))} style={selStyle}>
            {dataYears.map(y=><option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div style={{display:"flex",gap:6,alignItems:"flex-end",height:120,overflowX:"auto",paddingBottom:4}}>
        {data.length===0
          ? <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:12,height:120}}>No data for this period</div>
          : data.map(w=>{
            const pos = w.profit>=0;
            const pct = Math.max((Math.abs(w.profit)/maxAbs)*90,8);
            return (
              <div key={w.key} style={{flex:"0 0 44px",width:44,display:"flex",flexDirection:"column",alignItems:"center",gap:3}}>
                <div style={{fontSize:9,fontWeight:600,color:pos?"var(--success)":"var(--danger)"}}>{(pos?"+$":"-$")+fmt2(Math.abs(w.profit))}</div>
                <div style={{width:"75%",height:`${pct}%`,borderRadius:"3px 3px 0 0",background:pos?"var(--success)":"var(--danger)",opacity:0.9,minHeight:8}}/>
                <div style={{fontSize:9,color:"var(--text-faint)"}}>{w.label}</div>
                <div style={{fontSize:8,color:"var(--text-faint)",opacity:0.6}}>{w.dateLabel}</div>
              </div>
            );
          })
        }
      </div>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:10,fontSize:11,color:"var(--text-faint)"}}>
        <span>Weeks: {data.filter(w=>w.profit!==0).length}</span>
        <span>Profitable: {data.filter(w=>w.profit>0).length}</span>
        <span>Trades: {data.reduce((s,w)=>s+w.trades,0)}</span>
      </div>
    </div>
  );
}

function VirtualHeatmap({ trades }) {
  const [view, setView] = React.useState("pnl"); // pnl | signals | winrate
  const [period, setPeriod] = React.useState("3M");
  const filteredByPeriod = React.useMemo(()=>{
    if(period==="All") return trades||[];
    const days={"1M":30,"3M":90,"6M":180,"1Y":365}[period]||90;
    return (trades||[]).filter(t=>new Date(t.closeTime)>=new Date(Date.now()-days*86400000));
  },[trades,period]);
  const { cells, dates } = React.useMemo(()=>{
    const map = {};
    (filteredByPeriod||[]).forEach(t=>{
      const d = new Date(t.closeTime);
      const date = d.toISOString().slice(0,10);
      const hour = d.getUTCHours();
      const key = `${date}-${hour}`;
      if(!map[key]) map[key] = {date,hour,trades:0,pnl:0,wins:0};
      map[key].trades += 1; map[key].pnl += t.pnl;
      if(t.pnl>0) map[key].wins += 1;
    });
    const dates = [...new Set(Object.values(map).map(c=>c.date))].sort();
    return { cells: map, dates };
  },[filteredByPeriod]);
  if(dates.length===0) return null;
  const HOURS = Array.from({length:24},(_,i)=>i);
  const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const allCells = Object.values(cells);

  const getValue = c => {
    if(!c||c.trades===0) return 0;
    if(view==="pnl") return c.pnl;
    if(view==="signals") return c.trades;
    if(view==="winrate") return c.trades>0 ? c.wins/c.trades*100 : 0;
    return 0;
  };
  const allVals = allCells.map(c=>getValue(c)).filter(v=>v!==0);
  const maxVal = allVals.length ? Math.max(...allVals.map(Math.abs)) : 1;

  const getColor = c => {
    if(!c||c.trades===0) return "var(--bg-elevated)";
    const val = getValue(c);
    if(view==="signals") {
      const intensity = Math.min(val/maxVal,1);
      return `rgba(99,179,237,${0.15+intensity*0.7})`;
    }
    if(view==="winrate") {
      if(val>=60) return `rgba(34,197,94,${0.3+(val-60)/40*0.5})`;
      if(val>=40) return `rgba(245,158,11,0.3)`;
      return `rgba(239,68,68,0.4)`;
    }
    // pnl
    const intensity = Math.min(Math.abs(val)/maxVal,1);
    return val>=0 ? `rgba(34,197,94,${0.15+intensity*0.7})` : `rgba(239,68,68,${0.15+intensity*0.7})`;
  };

  const fmtVal = c => {
    if(!c||c.trades===0) return "";
    if(view==="pnl") return (c.pnl>=0?"+":"")+fmt2(Math.abs(c.pnl));
    if(view==="signals") return c.trades;
    if(view==="winrate") return Math.round(c.wins/c.trades*100)+"%";
    return "";
  };

  const fmtDateLabel = d => { const dt=new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; };
  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:11};

  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Virtual Trading Hours Heatmap</div>
        <div style={{display:"flex",gap:4}}>
          {["1M","3M","6M","1Y","All"].map(p=>(
            <button key={p} onClick={()=>setPeriod(p)} style={{padding:"3px 10px",fontSize:11,borderRadius:20,border:`1px solid ${period===p?"var(--accent)":"var(--border)"}`,background:period===p?"var(--accent)":"transparent",color:period===p?"#fff":"var(--text-muted)",cursor:"pointer"}}>{p}</button>
          ))}
        </div>
        <select value={view} onChange={e=>setView(e.target.value)} style={selStyle}>
          <option value="pnl">Net P&L</option>
          <option value="signals">Signal Count</option>
          <option value="winrate">Win Rate</option>
        </select>
      </div>
      <div style={{fontSize:11,color:"var(--text-faint)",marginBottom:10}}>P&L by date (UTC hour) · hover for details</div>
      <div style={{overflowX:"auto",overflowY:"auto",maxHeight:350}}>
        <div style={{display:"grid",gridTemplateColumns:"36px repeat(24,1fr)",gap:2,minWidth:600}}>
          <div style={{fontSize:9,color:"var(--text-faint)",textAlign:"right",paddingRight:4}}>Date</div>
          {HOURS.map(h=><div key={h} style={{fontSize:9,color:"var(--text-faint)",textAlign:"center",paddingBottom:2}}>{h}</div>)}
          {dates.map(date=>{
            const dt=new Date(date); const dayName=DAY_NAMES[dt.getUTCDay()];
            return (
              <React.Fragment key={date}>
                <div style={{fontSize:9,color:"var(--text-faint)",display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"center",paddingRight:4,lineHeight:1.2}}>
                  <span style={{fontWeight:600}}>{fmtDateLabel(date)}</span>
                  <span style={{fontSize:8,opacity:0.6}}>{dayName}</span>
                </div>
                {HOURS.map(h=>{
                  const c = cells[`${date}-${h}`];
                  return (
                    <div key={h}
                      title={c&&c.trades>0?`${date} ${h}:00 UTC — ${c.trades} signals, P&L: ${(c.pnl>=0?"+$":"-$")+Math.abs(c.pnl).toFixed(2)}, Win: ${c.trades>0?Math.round(c.wins/c.trades*100):0}%`:""}
                      style={{height:20,minWidth:20,borderRadius:3,background:getColor(c),
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontSize:8,fontWeight:600,color:c&&c.trades>0?"var(--text-main)":"transparent",
                        cursor:c&&c.trades>0?"pointer":"default",
                        border:"1px solid rgba(255,255,255,0.04)"}}>
                      {fmtVal(c)}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <div style={{fontSize:10,color:"var(--text-faint)",marginTop:6,textAlign:"center"}}>Hour (UTC) — hover cell for details</div>
    </div>
  );
}

function VirtualBias({ trades }) {
  const stats = React.useMemo(()=>{
    let buyT=0,sellT=0,buyP=0,sellP=0;
    (trades||[]).forEach(t=>{
      if((t.direction||"").toUpperCase()==="BUY"){buyT++;buyP+=t.pnl;}
      else {sellT++;sellP+=t.pnl;}
    });
    const total=buyT+sellT;
    return {buyT,sellT,buyP,sellP,total,buyPct:total?buyT/total*100:0,sellPct:total?sellT/total*100:0};
  },[trades]);
  if(stats.total===0) return null;
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:14}}>Long vs Short Bias (Virtual)</div>
      <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{flex:1,minWidth:200}}>
          <div style={{display:"flex",height:10,borderRadius:6,overflow:"hidden",marginBottom:6}}>
            <div style={{width:`${stats.buyPct}%`,background:"var(--success)"}}/>
            <div style={{width:`${stats.sellPct}%`,background:"var(--danger)"}}/>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span style={{fontSize:11,color:"var(--success)",fontWeight:600}}>Long {fmtPct(stats.buyPct)}</span>
            <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Short {fmtPct(stats.sellPct)}</span>
          </div>
        </div>
        <div style={{display:"flex",gap:16}}>
          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Buy Signals</div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>{stats.buyT}</div>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>{(stats.buyP>=0?"+$":"-$")+fmt2(Math.abs(stats.buyP))} P&L</div>
          </div>
          <div>
            <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Sell Signals</div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--danger)"}}>{stats.sellT}</div>
            <div style={{fontSize:10,color:"var(--text-faint)"}}>{(stats.sellP>=0?"+$":"-$")+fmt2(Math.abs(stats.sellP))} P&L</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VirtualPerPairTable({ trades }) {
  const rows = React.useMemo(()=>{
    const map = {};
    (trades||[]).forEach(t=>{
      if(!map[t.pair]) map[t.pair]={pair:t.pair,trades:0,wins:0,rrSum:0,pnl:0};
      const r=map[t.pair];
      r.trades+=1; r.pnl+=t.pnl; r.rrSum+=t.rr||0;
      if(t.pnl>0) r.wins+=1;
    });
    return Object.values(map).sort((a,b)=>b.trades-a.trades);
  },[trades]);
  if(rows.length===0) return null;
  return (
    <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20,marginBottom:16}}>
      <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:14}}>Virtual Performance Per Pair</div>
      <div style={{overflowX:"auto"}}>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead><tr>{["Pair","Signals","Win%","Avg RR","Net P&L (Virtual)"].map(h=><TH key={h}>{h}</TH>)}</tr></thead>
          <tbody>
            {rows.map(r=>(
              <tr key={r.pair}>
                <TD style={{fontWeight:700}}>{r.pair}</TD>
                <TD>{r.trades}</TD>
                <TD>{fmtPct(r.trades?r.wins/r.trades*100:0)}</TD>
                <TD>1:{fmt2(r.trades?r.rrSum/r.trades:0)}</TD>
                <TD style={{fontWeight:700,color:r.pnl>=0?"var(--success)":"var(--danger)"}}>{(r.pnl>=0?"+$":"-$")+fmt2(Math.abs(r.pnl))}</TD>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AnalystPerformanceTab({ virtualAlpha, vaLoading, alphaRank, riskPct, onRiskChange }) {
  // virtualAlpha = backend exact same AlphaRank Calculator as trader
  const va      = virtualAlpha;
  const vaReady = !!va;
  const grade   = va?.grade || "—";
  const score   = va?.alphaScore || 0;
  const pillars = va?.pillars || [];
  const flags   = va?.riskFlags?.items || [];
  const netPnL  = va?.netPnL || 0;
  const roi     = va?.roi || 0;
  const maxDD   = va?.maxDD || 0;
  const balance = va?.virtualBalance || VIRTUAL_INITIAL;
  const curve   = React.useMemo(()=>(va?.equityCurve||[]).map(p=>({t:p.time,v:p.equity})),[va]);
  const [customRisk, setCustomRisk] = React.useState(String(riskPct));
  React.useEffect(()=>{ setCustomRisk(String(riskPct)); }, [riskPct]);
  const [period, setPeriod] = React.useState("All");
  const { filteredCurve, filteredTrades } = React.useMemo(()=>{
    const allTrades = va?.trades||[];
    if(period==="All") return { filteredCurve:curve, filteredTrades:allTrades };
    const days = {"1M":30,"3M":90,"6M":180,"1Y":365}[period]||99999;
    const cutoff = Date.now() - days*86400000;
    return {
      filteredCurve: curve.filter(p=>new Date(p.t).getTime()>=cutoff),
      filteredTrades: allTrades.filter(t=>new Date(t.closeTime).getTime()>=cutoff)
    };
  },[period, curve, va]);
  const GRADE_COL = {"A+":"var(--success)","A":"var(--success)","A-":"var(--success)",
    "B+":"var(--accent)","B":"var(--accent)","B-":"var(--accent)",
    "C+":"var(--warning)","C":"var(--warning)","C-":"var(--warning)",
    "D":"var(--warning)","F":"var(--danger)"};
  const gradeCol = g => GRADE_COL[g] || "var(--text-muted)";
  const sevColor = s => s==="MAJOR"?"var(--danger)":s==="MINOR"?"var(--warning)":"var(--accent)";
  const sevBg    = s => s==="MAJOR"?"rgba(255,92,122,0.08)":s==="MINOR"?"rgba(254,202,87,0.08)":"rgba(167,139,250,0.12)";

  return (
    <div style={{padding:"24px"}}>
      {/* Loading / not-ready guard */}
      {vaLoading&&<div style={{padding:48,textAlign:"center",color:"var(--text-muted)"}}>
        <div style={{fontSize:14,marginBottom:6}}>   Calculating virtual account...</div>
        <div style={{fontSize:11}}>Running exact same AlphaRank formula as traders</div>
      </div>}
      {!vaLoading&&!vaReady&&<div style={{padding:48,textAlign:"center",color:"var(--text-muted)"}}>
        <div style={{fontSize:28,marginBottom:10}}></div>
        <div style={{fontSize:14,fontWeight:700,marginBottom:6}}>Need 10+ closed signals</div>
        <div style={{fontSize:12}}>Virtual AlphaScore appears once you have enough signal history</div>
      </div>}
      {!vaLoading&&vaReady&&<>
      {/* Virtual Account Banner */}
      <div style={{background:"rgba(78,154,241,0.08)",border:"1px solid rgba(78,154,241,0.25)",
        borderRadius:"var(--radius-md)",padding:"14px 18px",marginBottom:20,
        display:"flex",alignItems:"center",gap:14}}>
        <span style={{fontSize:22,flexShrink:0}}></span>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:"var(--accent)"}}>Virtual Account — Exact Same AlphaRank™ Formula as Trader</div>
          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:3}}>
            Starting balance&nbsp;<strong style={{color:"var(--text-main)"}}>$1,000.00</strong>
            &nbsp;·&nbsp;Compounding risk&nbsp;<strong style={{color:"var(--text-main)"}}>{va?.riskPct||riskPct}% of equity per signal</strong>
            &nbsp;·&nbsp;<strong style={{color:"var(--text-main)"}}>{va?.totalSignals||0}</strong> closed signals
            &nbsp;·&nbsp;<strong style={{color:"var(--text-main)"}}>{va?.daysActive||0}</strong> days active
          </div>
        </div>
      </div>

      {/* Risk Level Simulator */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:20,flexWrap:"wrap",
        background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",padding:"12px 16px"}}>
        <span style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:0.6}}>Simulate Risk Per Trade:</span>
        {[0.5,1.5,3.0].map(p=>(
          <button key={p} onClick={()=>onRiskChange(p)} style={{
            padding:"6px 14px",borderRadius:"var(--radius-md)",border:"1px solid",
            borderColor: riskPct===p?"var(--accent)":"var(--border)",
            background: riskPct===p?"rgba(78,154,241,0.12)":"transparent",
            color: riskPct===p?"var(--accent)":"var(--text-muted)",
            fontSize:12,fontWeight:700,cursor:"pointer"
          }}>{p}%{p===1.5?" (default)":""}</button>
        ))}
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <input type="number" min="0.1" max="10" step="0.1" value={customRisk}
            onChange={e=>setCustomRisk(e.target.value)}
            onKeyDown={e=>{ if(e.key==="Enter"){ const v=parseFloat(customRisk); if(v>=0.1&&v<=10) onRiskChange(v); } }}
            style={{width:60,padding:"6px 8px",borderRadius:"var(--radius-md)",border:"1px solid var(--border)",
              background:"var(--bg-elevated)",color:"var(--text-main)",fontSize:12}}/>
          <span style={{fontSize:12,color:"var(--text-muted)"}}>%</span>
          <button onClick={()=>{ const v=parseFloat(customRisk); if(v>=0.1&&v<=10) onRiskChange(v); }}
            style={{padding:"6px 12px",borderRadius:"var(--radius-md)",border:"1px solid var(--border)",
              background:"transparent",color:"var(--accent)",fontSize:12,fontWeight:700,cursor:"pointer"}}>Apply</button>
        </div>
        {vaLoading&&<span style={{fontSize:11,color:"var(--text-muted)"}}>   recalculating...</span>}
      </div>

      {/* Stats Grid — same layout as TraderDashboard */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:10,marginBottom:20}}>
        {[
          {label:"Virtual Balance",   value:"$"+fmt2(balance),                                       color:"var(--text-main)"},
          {label:"Net P&L",           value:(netPnL>=0?"+$":"-$")+fmt2(Math.abs(netPnL)),            color:netPnL>=0?"var(--success)":"var(--danger)"},
          {label:"ROI",               value:(roi>=0?"+":"")+fmt2(roi)+"%",                            color:roi>=0?"var(--success)":"var(--danger)"},
          {label:"Win Rate",          value:fmtPct(va?.winRate||0),                                   color:"var(--text-main)"},
          {label:"Total Signals",     value:va?.totalSignals||0,                                      color:"var(--text-main)"},
          {label:"Profit Factor",     value:va?.profitFactor>0?fmt2(va.profitFactor):"—",             color:"var(--text-main)"},
          {label:"Max DD (Virtual)",  value:fmt2(maxDD)+"%",                                          color:maxDD>20?"var(--danger)":maxDD>10?"var(--warning)":"var(--success)"},
          {label:"Avg R:R",           value:alphaRank?.avgRR>0?"1:"+fmt2(alphaRank.avgRR):"—",       color:"var(--text-main)"},
          {label:"Avg Win / Loss",    value:"$"+fmt2(va?.avgWin||0)+" / $"+fmt2(va?.avgLoss||0),     color:"var(--text-main)"},
          {label:"Peak Balance",      value:"$"+fmt2(va?.peakBalance||VIRTUAL_INITIAL),               color:"var(--text-main)"},
        ].map(s=>(
          <div key={s.label} style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",
            borderRadius:"var(--radius-lg)",padding:"14px 18px"}}>
            <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,letterSpacing:"0.07em",
              textTransform:"uppercase",marginBottom:6}}>{s.label}</div>
            <div style={{fontSize:String(s.value).length>9?15:18,fontWeight:700,color:s.color,
              lineHeight:1.1,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* AlphaRank + Risk Flags — 2 column */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16,marginBottom:16}}>

        {/* AlphaRank */}
        <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:16}}>AlphaRank Analysis</div>

          <div style={{display:"flex",gap:20,alignItems:"flex-start"}}>
            {/* Grade Circle */}
            <div style={{textAlign:"center",flexShrink:0}}>
              <div style={{width:80,height:80,borderRadius:"50%",
                border:`4px solid ${gradeCol(grade)}`,
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                margin:"0 auto 8px"}}>
                <div style={{fontSize:22,fontWeight:800,color:gradeCol(grade)}}>{grade}</div>
              </div>
              <div style={{fontSize:18,fontWeight:700,color:gradeCol(grade)}}>{score>0?fmt2(score):"—"}</div>
              <div style={{fontSize:11,color:"var(--text-faint)"}}>Virtual Score™</div>
            </div>
            {/* Pillars P1-P7 */}
            <div style={{flex:1}}>
              {pillars.length>0 ? pillars.map(p=>(
                <div key={p.code} style={{marginBottom:10}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontSize:12,color:"var(--text-muted)",fontWeight:500}}>{p.name}</span>
                    <span style={{fontSize:12,fontWeight:700,color:"var(--accent)"}}>{fmt2(p.score)}</span>
                  </div>
                  <div style={{height:5,background:"var(--bg-elevated)",borderRadius:"var(--radius-full)"}}>
                    <div style={{height:5,borderRadius:"var(--radius-full)",background:"var(--accent)",
                      width:`${Math.min(p.score,100)}%`,transition:"width 0.6s ease"}}/>
                  </div>
                  {p.reason&&<div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>{p.reason}</div>}
                </div>
              )) : (
                <div style={{color:"var(--text-faint)",fontSize:12,textAlign:"center",padding:"20px 0"}}>
                  Need 20+ closed signals for full analysis
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Risk Flags */}
        <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",padding:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
            <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Risk Flags</div>
            {flags.length>0&&(
              <span style={{fontSize:11,padding:"2px 8px",borderRadius:"var(--radius-sm)",
                background:"var(--danger-soft)",color:"var(--danger)"}}>
                {flags.length} detected
              </span>
            )}
          </div>
          {flags.length>0 ? flags.map((f,i)=>(
            <div key={i} style={{background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",
              padding:"10px 14px",marginBottom:8,borderLeft:`3px solid ${sevColor(f.severity)}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:12,fontWeight:600,color:"var(--text-main)"}}>{f.title}</span>
                <span style={{fontSize:10,fontWeight:600,padding:"2px 7px",
                  borderRadius:"var(--radius-sm)",background:sevBg(f.severity),color:sevColor(f.severity)}}>
                  {f.severity}
                </span>
              </div>
              <div style={{fontSize:11,color:"var(--text-muted)",marginTop:4}}>{f.desc}</div>
            </div>
          )) : (
            <div style={{textAlign:"center",padding:"30px 0",color:"var(--text-faint)"}}>
              <div style={{fontSize:28,marginBottom:8}}>  </div>
              <div style={{fontSize:13}}>No risk flags detected</div>
            </div>
          )}
          {alphaRank?.riskProfile&&alphaRank.riskProfile!=="Unknown"&&(
            <div style={{marginTop:12,background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",padding:"10px 14px"}}>
              <div style={{fontSize:11,color:"var(--text-faint)",marginBottom:4}}>Signal Style</div>
              <div style={{fontSize:15,fontWeight:700,color:
                alphaRank.riskProfile==="Conservative"?"var(--success)":
                alphaRank.riskProfile==="Balanced"?"var(--accent)":
                alphaRank.riskProfile==="Aggressive"?"var(--warning)":"var(--danger)"}}>
                {alphaRank.riskProfile}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Virtual Equity Curve */}
      {/* Period Selector — applies to all charts below */}
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:16,flexWrap:"wrap",
        background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",padding:"8px 14px"}}>
        <span style={{fontSize:11,fontWeight:700,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:0.6,marginRight:4}}>Period:</span>
        {["1M","3M","6M","1Y","All"].map(p=>(
          <button key={p} onClick={()=>setPeriod(p)} style={{
            padding:"5px 12px",borderRadius:"var(--radius-md)",border:"1px solid",
            borderColor:period===p?"var(--accent)":"var(--border)",
            background:period===p?"rgba(78,154,241,0.12)":"transparent",
            color:period===p?"var(--accent)":"var(--text-muted)",
            fontSize:12,fontWeight:700,cursor:"pointer"
          }}>{p}</button>
        ))}
        <span style={{fontSize:10,color:"var(--text-faint)",marginLeft:4}}>
          {filteredTrades.length} signals · {filteredCurve.length} data points
        </span>
      </div>
      <VirtualEquityChart curve={filteredCurve} netPnL={netPnL} roi={roi} riskPct={riskPct}/>
      <VirtualDrawdownChart curve={filteredCurve} />
      <VirtualMonthlyChart trades={filteredTrades} />
      <VirtualWeeklyChart trades={filteredTrades} />
      <VirtualHeatmap trades={filteredTrades} />
      <VirtualBias trades={filteredTrades} />
      <VirtualPerPairTable trades={filteredTrades} />
      </>}
    </div>
  );
}


// ── Analyst Onboarding / Welcome Checklist ───────────────────────────────────
function AnalystOnboarding({ profile, signalSets, signals, onCreateSet, onGoToProfile, onGoToSend }) {
  const hasProfile = !!(profile && (profile.bio || profile.country || profile.phone ||
    (profile.displayName && profile.displayName !== profile.email)));
  const hasSignalSet = signalSets.length > 0;
  const hasSignal = signals.filter(s => s.status !== "CANCELLED_MANUAL" && s.status !== "CANCELLED").length > 0;

  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem("analyst_onboarding_v2") === "1"
  );

  const allRequired = hasProfile && hasSignalSet && hasSignal;
  React.useEffect(() => {
    if (allRequired) { localStorage.setItem("analyst_onboarding_v2","1"); setDismissed(true); }
  }, [allRequired]);

  if (dismissed) return null;

  const steps = [
    {
      n: 1,
      title: "Complete your public profile",
      desc: "Add your display name, bio & country — shown to investors on your public analyst page.",
      done: hasProfile,
      required: true,
      action: !hasProfile ? { label: "Go to Profile →", fn: onGoToProfile } : null,
      locked: false,
    },
    {
      n: 2,
      title: "Create your first Signal Set",
      desc: "Your Signal Set is your brand as a signal provider. Max 5 signal sets per account.",
      done: hasSignalSet,
      required: true,
      action: !hasSignalSet ? { label: "+ Create Signal Set", fn: onCreateSet } : null,
      locked: !hasProfile,
    },
    {
      n: 3,
      title: "Send your first signal",
      desc: "Publish a trade idea with entry, SL, and TP to start building your track record.",
      done: hasSignal,
      required: true,
      action: (!hasSignal && hasSignalSet) ? { label: " Send Signal →", fn: onGoToSend } : null,
      locked: !hasSignalSet,
    },
    {
      n: 4,
      title: "Share your public profile link",
      desc: "Share your signal page with traders and investors to attract your first subscribers.",
      done: false,
      required: false,
      action: null,
      locked: !hasSignal,
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const pct = Math.round(doneCount / steps.length * 100);

  return (
    <div style={{
      background: "var(--bg-surface)",
      border: "1px solid var(--border)",
      borderLeft: "3px solid var(--accent)",
      borderRadius: "var(--radius-xl)",
      padding: "24px 28px",
      marginBottom: 20,
    }}>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:16, flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ fontSize:17, fontWeight:800, color:"var(--text-main)", marginBottom:4 }}>
            Welcome to SignalSync™ 
          </div>
          <div style={{ fontSize:12, color:"var(--text-muted)" }}>
            Complete the steps below to fully activate your analyst dashboard.
          </div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:14 }}>
          <div style={{ textAlign:"center" }}>
            <div style={{ fontSize:20, fontWeight:900, color:"var(--accent)" }}>{doneCount}/{steps.length}</div>
            <div style={{ fontSize:10, color:"var(--text-muted)" }}>steps done</div>
          </div>
          <button
            onClick={()=>{ localStorage.setItem("analyst_onboarding_v2","1"); setDismissed(true); }}
            style={{ padding:"4px 12px", fontSize:11, borderRadius:"var(--radius-md)",
              border:"1px solid var(--border)", background:"transparent",
              color:"var(--text-muted)", cursor:"pointer" }}>
            Dismiss
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ height:4, background:"var(--border)", borderRadius:2, marginBottom:20 }}>
        <div style={{ height:"100%", width:`${pct}%`, background:"var(--accent)", borderRadius:2,
          transition:"width 0.6s ease" }}/>
      </div>

      {/* Steps */}
      <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
        {steps.map(s => (
          <div key={s.n} style={{
            display:"flex", gap:14, alignItems:"center",
            padding:"14px 16px",
            background: s.done ? "rgba(29,209,161,0.06)" : s.locked ? "rgba(255,255,255,0.01)" : "var(--bg-elevated)",
            border:`1px solid ${s.done ? "rgba(29,209,161,0.25)" : "var(--border)"}`,
            borderRadius:"var(--radius-md)",
            opacity: s.locked ? 0.45 : 1,
            transition:"all 0.2s",
            pointerEvents: s.locked ? "none" : "auto",
          }}>
            {/* Step circle */}
            <div style={{
              width:30, height:30, borderRadius:"50%", flexShrink:0,
              background: s.done ? "var(--success)" : "var(--bg-elevated)",
              border: s.done ? "none" : "2px solid var(--border)",
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize: s.done ? 14 : 12, fontWeight:800,
              color: s.done ? "#fff" : "var(--text-muted)",
            }}>
              {s.done ? "✓" : s.n}
            </div>

            {/* Text */}
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3, flexWrap:"wrap" }}>
                <span style={{ fontSize:13, fontWeight:700,
                  color: s.done ? "var(--text-muted)" : "var(--text-main)",
                  textDecoration: s.done ? "line-through" : "none" }}>
                  {s.title}
                </span>
                {!s.done && s.required && (
                  <span style={{ fontSize:9, fontWeight:800, letterSpacing:0.6,
                    color:"var(--accent)", background:"rgba(78,154,241,0.12)",
                    padding:"1px 6px", borderRadius:10, border:"1px solid rgba(78,154,241,0.25)" }}>
                    REQUIRED
                  </span>
                )}
                {!s.required && !s.done && (
                  <span style={{ fontSize:9, fontWeight:600, letterSpacing:0.6,
                    color:"var(--text-muted)", background:"rgba(255,255,255,0.06)",
                    padding:"1px 6px", borderRadius:10 }}>
                    OPTIONAL
                  </span>
                )}
              </div>
              <div style={{ fontSize:11, color:"var(--text-muted)", lineHeight:1.5 }}>{s.desc}</div>
            </div>

            {/* Action / status */}
            {s.done ? (
              <span style={{ fontSize:12, color:"var(--success)", fontWeight:700, flexShrink:0 }}>✓ Done</span>
            ) : s.locked ? (
              <span style={{ fontSize:10, color:"var(--text-muted)", flexShrink:0 }}>
                🔒 Step {s.n - 1} first
              </span>
            ) : s.action ? (
              <button onClick={s.action.fn} style={{
                padding:"7px 16px", borderRadius:"var(--radius-md)", border:"none",
                background:"var(--accent)", color:"#fff",
                fontSize:12, fontWeight:700, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap",
              }}>
                {s.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {/* Footer: public link */}
      <div style={{ marginTop:16, paddingTop:12, borderTop:"1px solid var(--border)",
        fontSize:11, color:"var(--text-muted)", textAlign:"center" }}>
        {signalSets.length > 0 ? (
          <>Your public profile link:{" "}
            <a href={`/strategy/analyst/${signalSets[0].id}`} target="_blank" rel="noopener noreferrer"
              style={{ color:"var(--accent)", textDecoration:"none", fontWeight:600 }}>
              /strategy/analyst/{signalSets[0].id}
            </a>
          </>
        ) : (
          <span>Complete steps above to get your public profile link.</span>
        )}
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
const AnalystDashboard = () => {
  usePageTitle("SignalSync™ Dashboard");
  const [loading,setLoading]           = useState(true);
  const [error,setError]               = useState(null);
  const [summary,setSummary]           = useState({});
  const [signalSets,setSignalSets]     = useState([]);
  const [selectedSetId,setSelectedSetId] = useState("");
  const [signals,setSignals]           = useState([]);
  const [alphaRank,setAlphaRank]       = useState(null);
  const [showCreateSet,setShowCreateSet] = useState(false);
  const [editingSet,setEditingSet]     = useState(null);
  const [editingSig,setEditingSig]     = useState(null);
  const [cancelId,setCancelId]         = useState(null);
  const [tab,setTab]                   = useState("summary");
  const [subscribers, setSubscribers] = useState(null);
  const [virtualAlpha, setVirtualAlpha] = useState(null);
  const [profile, setProfile] = useState(null);

  const [vaLoading, setVaLoading] = useState(false);
  const [riskPct, setRiskPct] = useState(1.5);

  useEffect(()=>{
    if(!selectedSetId) return;
    setVaLoading(true);
    setVirtualAlpha(null);
    api.getAnalystVirtualAlphaRank(selectedSetId, riskPct)
      .then(d=>setVirtualAlpha(d.alphaRank||null))
      .catch(e=>console.error("VirtualAlphaRank:",e))
      .finally(()=>setVaLoading(false));
  },[selectedSetId, riskPct]);

  useEffect(()=>{
    if(tab !== "subscribers") return;
    if(subscribers) return;
    const token = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch("/api/analyst/my-subscribers", { headers:{ Authorization:`Bearer ${token}` }})
      .then(r=>r.json()).then(d=>{ if(d.ok) setSubscribers(d); });
  },[tab]);

  const fetchData = async (setIdParam) => {
    const sid = setIdParam !== undefined ? setIdParam : selectedSetId;
    try {
      setLoading(true);
      const [dash, sigs] = await Promise.all([
        api.getAnalystDashboard(),
        api.getAnalystSignals(),
      ]);
      const sets = dash.signalSets||[];
      const resolvedSid = sid || (sets[0]?.id || "");
      const arRes = resolvedSid ? await api.getAnalystAlphaRank(resolvedSid) : {alphaRank:null};
      setSummary(dash.summary||{});
      setSignalSets(sets);
      if(!selectedSetId && sets.length > 0) setSelectedSetId(sets[0].id);
      setSignals(sigs.signals||[]);
      setAlphaRank(arRes.alphaRank||null);
      setError(null);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  };

  useEffect(()=>{
    fetchData();
    // Fetch user profile for onboarding completion check
    const _tok = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
    fetch("/api/profile", { headers:{ Authorization:`Bearer ${_tok}` }})
      .then(r=>r.json())
      .then(d=>{ setProfile(d.profile || d || null); })
      .catch(()=>{});
  },[]);

  // Auto-refresh setiap 30 detik, hanya saat tab Active atau Running
  useEffect(()=>{
    if(!["active","running"].includes(tab)) return;
    const interval = setInterval(()=>{ fetchData(); }, 30000);
    return ()=>clearInterval(interval);
  },[tab, selectedSetId]);

  const handleCancel = async (id) => {
    if(!window.confirm("Cancel this signal?")) return;
    setCancelId(id);
    try {
      await api.cancelAnalystSignal(id);
      setSignals(prev=>prev.map(s=>s.id===id?{...s,status:"CANCELLED_MANUAL"}:s));
    } catch(e) { alert("Failed: "+e.message); }
    finally { setCancelId(null); }
  };

  const userRole = (localStorage.getItem("user_role") || "").toLowerCase();
  const isAdmin = userRole === "admin" || localStorage.getItem("is_admin") === "true";
  const readOnly = isImpersonating();

  const filteredSignals = selectedSetId ? signals.filter(s=>s.set_id===selectedSetId) : signals;
  const pendingSignals  = filteredSignals.filter(s=>["PENDING","PENDING_LIMIT","OPEN","BRS_PENDING_BREAK","BRS_ARMED"].includes(s.status));
  const runningSignals  = filteredSignals.filter(s=>s.status==="RUNNING");
  const historySignals  = filteredSignals.filter(s=>["CLOSED_TP","CLOSED_SL","CANCELLED_MANUAL","BRS_INVALIDATED"].includes(s.status));
  const canCreateSet    = signalSets.length < 5;
  const hasSignalSets   = signalSets.length > 0;

  const TABS = [
    {id:"summary",        label:"Overview"},
    {id:"performance",    label:" Performance"},
    {id:"send_signal",    label:" Send Signal", readOnlyHide: true},
    {id:"active",         label:`Active (${pendingSignals.length})`},
    {id:"running",        label:`Running (${runningSignals.length})`},
    {id:"history",        label:`History (${historySignals.length})`},
    {id:"subscribers",    label:"Followers"},
    {id:"signal_sets",    label:"Signal Sets"},
    {id:"settings",       label:" Settings"},
  ];

  if(loading) return <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto", display:"flex", alignItems:"center", justifyContent:"center", minHeight:400 }}><div style={{ color:"var(--text-muted)", fontSize:13 }}>Loading...</div></div>;
  if(error) return <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto" }}><div style={{ background:"rgba(255,92,122,0.1)", border:"1px solid rgba(255,92,122,0.3)", borderRadius:"var(--radius-lg)", padding:"16px 20px", color:"var(--danger)", fontSize:13 }}>Failed to load: {error}</div></div>;

  return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto" }}>
      {readOnly && (
        <div style={{
          background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.4)",
          borderRadius:"var(--radius-md)", padding:"10px 20px", marginBottom:16,
          fontSize:13, color:"var(--warning)", display:"flex", alignItems:"center", gap:8
        }}>
             <strong>Read-only mode</strong> — You are viewing this dashboard as an impersonated user. All actions are disabled.
        </div>
      )}
      {showCreateSet&&<CreateSignalSetCapital currentCount={signalSets.length} onClose={()=>setShowCreateSet(false)} onSuccess={()=>{setShowCreateSet(false);fetchData();}}/>}
      {editingSet&&<EditSignalSetCapital signalSet={editingSet} onClose={()=>setEditingSet(null)} onSuccess={()=>{setEditingSet(null);fetchData();}}/>}
      {editingSig&&<EditSignalCapital signal={editingSig} onClose={()=>setEditingSig(null)} onSuccess={()=>{setEditingSig(null);fetchData();}}/>}

      {/* Header — Trader Dashboard style */}
      <div style={{display:"flex",flexWrap:"wrap",gap:12,justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <h1 style={{fontSize:22,fontWeight:700,color:"var(--text-main)",margin:0}}>Analyst Dashboard</h1>
          <p style={{color:"var(--text-muted)",fontSize:13,margin:"4px 0 0"}}>
            Signal analytics & AlphaRank™
          </p>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {signalSets.length>0&&(
            <select value={selectedSetId} onChange={e=>{setSelectedSetId(e.target.value);fetchData(e.target.value);}}
              style={{background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",
                borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:13}}>
              {signalSets.map(s=>(<option key={s.id} value={s.id}>{s.name}</option>))}
            </select>
          )}
          {hasSignalSets&&!readOnly&&<button style={{...btnPrimary,fontSize:13,padding:"8px 16px"}} onClick={()=>setTab("send_signal")}> Send Signal</button>}
          {canCreateSet&&!readOnly&&<button style={{...btnSecondary,borderColor:"var(--accent)",color:"var(--accent)"}} onClick={()=>setShowCreateSet(true)}>+ Signal Set</button>}
          <NotificationBell />
        </div>
      </div>

      <AnalystOnboarding
        profile={profile}
        signalSets={signalSets}
        signals={signals}
        onCreateSet={()=>setShowCreateSet(true)}
        onGoToProfile={()=>{ window.location.href="/profile"; }}
        onGoToSend={()=>setTab("send_signal")}
      />

      {!hasSignalSets&&(
        <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-xl)",
          padding:"40px 24px",textAlign:"center",marginBottom:16}}>
          <div style={{fontSize:32,marginBottom:12}}></div>
          <div style={{fontSize:17,fontWeight:700,color:"var(--text-main)",marginBottom:8}}>Welcome, Analyst </div>
          <div style={{fontSize:13,color:"var(--text-muted)",marginBottom:20,maxWidth:400,margin:"0 auto 20px"}}>
            Create your first Signal Set to start publishing trade ideas and tracking performance.
          </div>
          {canCreateSet&&<button style={btnPrimary} onClick={()=>setShowCreateSet(true)}>+ Create Signal Set</button>}
        </div>
      )}
      {/* Main Tabs */}
      <div style={{...card}}>
        {/* Tab Bar */}
        <div style={{ padding:"0 24px", borderBottom:"1px solid var(--border-subtle)", display:"flex", overflowX:"auto" }}>
          {TABS.filter(t => (!t.adminOnly || isAdmin) && (!t.readOnlyHide || !readOnly)).map(({id,label})=>(
            <button key={id} onClick={()=>setTab(id)} style={{
              padding:"14px 16px", border:"none", cursor:"pointer", fontSize:12, fontWeight:700,
              background:"transparent", color:tab===id?"var(--text-main)":"var(--text-muted)",
              borderBottom:tab===id?"2px solid var(--accent)":"2px solid transparent",
              transition:"all 0.15s", whiteSpace:"nowrap",
            }}>{label}</button>
          ))}
        </div>

        {/* ── Tab: Send Signal (split pane, no padding wrapper) ── */}
        {tab==="send_signal" && (
          hasSignalSets ? (
            <div style={{ padding:"20px 20px 0" }}>
              <ExecutionAccountCard signalSets={signalSets} />
              <SendSignalPane signalSets={signalSets} selectedSetId={selectedSetId} onSuccess={()=>fetchData()} />
            </div>
          ) : (
            <div style={{ padding:"48px 24px", textAlign:"center", color:"var(--text-muted)" }}>
              <div style={{ fontSize:32, marginBottom:12 }}></div>
              <div style={{ fontSize:14, fontWeight:700, marginBottom:6 }}>Buat Signal Set dulu</div>
              <div style={{ fontSize:12, marginBottom:16 }}>Kamu perlu minimal 1 Signal Set sebelum bisa kirim signal</div>
              <button style={btnPrimary} onClick={()=>setShowCreateSet(true)}>+ Create Signal Set</button>
            </div>
          )
        )}

        {tab==="summary"&&(
          <div style={{padding:"24px", display:"flex", flexDirection:"column", gap:14}}>
            {/* AlphaRank Hero — compact (full detail on Performance) */}
            <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",padding:"16px 18px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>AlphaRank</div>
                <button type="button" onClick={()=>setTab("performance")}
                  style={{background:"transparent",border:"1px solid var(--accent-border)",borderRadius:"var(--radius-full)",
                    padding:"4px 10px",fontSize:11,fontWeight:600,color:"var(--accent)",cursor:"pointer"}}>
                  Full analysis →
                </button>
              </div>
              {!virtualAlpha ? (
                <div style={{background:"var(--warning-soft)",border:"1px solid rgba(245,158,11,0.3)",borderRadius:"var(--radius-md)",padding:"12px 14px"}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--warning)",marginBottom:4}}>
                    {vaLoading ? "Calculating..." : "AlphaRank not available yet"}
                  </div>
                  <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.5}}>
                    Need enough closed signals. Virtual AlphaScore uses the same formula as traders.
                  </div>
                </div>
              ) : (
                <div style={{display:"grid",gridTemplateColumns:"auto minmax(0,1fr) minmax(120px,0.35fr)",gap:16,alignItems:"start"}}>
                  <div style={{textAlign:"center"}}>
                    <div style={{width:72,height:72,borderRadius:"50%",border:`4px solid ${GRADE_COLOR[virtualAlpha.grade]||"var(--text-muted)"}`,
                      display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 6px"}}>
                      <div style={{fontSize:22,fontWeight:800,color:GRADE_COLOR[virtualAlpha.grade]||"var(--text-muted)"}}>{virtualAlpha.grade}</div>
                    </div>
                    <div style={{fontSize:16,fontWeight:700,color:GRADE_COLOR[virtualAlpha.grade]||"var(--text-muted)"}}>{fmt2(virtualAlpha.alphaScore)}</div>
                    <div style={{fontSize:10,color:"var(--text-faint)"}}>Virtual Score</div>
                  </div>
                  <div>
                    <div style={{fontSize:11,color:"var(--text-faint)",fontWeight:600,marginBottom:8,letterSpacing:"0.04em"}}>TOP PILLARS</div>
                    {[...(virtualAlpha.pillars||[])].sort((a,b)=>(b.score||0)-(a.score||0)).slice(0,3).map(p=>(
                      <div key={p.code} style={{marginBottom:8}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                          <span style={{fontSize:12,color:"var(--text-muted)",fontWeight:500}}>{p.name}</span>
                          <span style={{fontSize:12,fontWeight:700,color:"var(--accent)"}}>{fmt2(p.score)}</span>
                        </div>
                        <div style={{height:4,background:"var(--bg-elevated)",borderRadius:"var(--radius-full)"}}>
                          <div style={{height:4,borderRadius:"var(--radius-full)",background:"var(--accent)",width:`${Math.min(p.score||0,100)}%`}}/>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div style={{background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",padding:"10px 12px"}}>
                    <div style={{fontSize:10,color:"var(--text-faint)",fontWeight:600,marginBottom:6}}>RISK SUMMARY</div>
                    <div style={{fontSize:12,fontWeight:600,color:(virtualAlpha.riskFlags?.items?.length||0)>0?"var(--danger)":"var(--success)",marginBottom:4}}>
                      {(virtualAlpha.riskFlags?.items?.length||0)>0
                        ? `${virtualAlpha.riskFlags.items.length} flag(s)`
                        : "No risk flags"}
                    </div>
                    {virtualAlpha.riskProfile?.label && (
                      <div style={{fontSize:11,color:"var(--text-muted)"}}>Style: {virtualAlpha.riskProfile.label}</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Primary 5 KPI — Overview only */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(5,minmax(0,1fr))",gap:12}}>
              {[
                {label:"Win Rate", value: alphaRank?.winRate>0?`${alphaRank.winRate.toFixed(1)}%`:"—", color:"var(--text-main)"},
                {label:"Cumulative R", value: (alphaRank?.cumulativeR!=null)?`${alphaRank.cumulativeR>=0?"+":""}${Number(alphaRank.cumulativeR).toFixed(2)}`:"—",
                  color:(alphaRank?.cumulativeR||0)>=0?"var(--success)":"var(--danger)"},
                {label:"Profit Factor", value: alphaRank?.profitFactor>0?Number(alphaRank.profitFactor).toFixed(2):"—", color:"var(--text-main)"},
                {label:"Closed", value: filteredSignals.filter(s=>s.status==="CLOSED_TP"||s.status==="CLOSED_SL").length, color:"var(--text-main)"},
                {label:"Open", value: pendingSignals.length+runningSignals.length, color:"var(--accent)"},
              ].map(c=>(
                <div key={c.label} style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"12px 14px"}}>
                  <div style={{fontSize:10,color:"var(--text-muted)",fontWeight:600,letterSpacing:"0.06em",textTransform:"uppercase",marginBottom:6}}>{c.label}</div>
                  <div style={{fontSize:18,fontWeight:700,color:c.color}}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* Charts stay on Overview (not duplicated on Performance) */}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))",gap:16}}>
              <div style={{background:"var(--bg-elevated-soft)",borderRadius:"var(--radius-md)",padding:"20px",border:"1px solid var(--border-subtle)"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                  <div style={{fontSize:12,fontWeight:800,color:"var(--text-main)"}}>Growth Channels (R:R Units)</div>
                  <div style={{fontSize:10,color:"var(--text-muted)"}}>Cumulative closed signals</div>
                </div>
                <GrowthChart signals={filteredSignals}/>
              </div>
              <div style={{background:"var(--bg-elevated-soft)",borderRadius:"var(--radius-md)",padding:"20px",border:"1px solid var(--border-subtle)"}}>
                <div style={{fontSize:12,fontWeight:800,color:"var(--text-main)",marginBottom:16}}>Signal Frequency</div>
                <FrequencyBars pending={pendingSignals.length} running={runningSignals.length}
                  wins={filteredSignals.filter(s=>s.status==="CLOSED_TP").length}
                  losses={filteredSignals.filter(s=>s.status==="CLOSED_SL").length}
                  cancelled={filteredSignals.filter(s=>s.status==="CANCELLED_MANUAL"||s.status==="CANCELLED"||s.status==="BRS_INVALIDATED"||s.status==="EXPIRED").length}
                  total={filteredSignals.filter(s=>!["DRAFT","BRS_PENDING_BREAK","BRS_ARMED","EXPIRED"].includes(s.status)).length}/>
              </div>
            </div>
          </div>
        )}


        {tab==="signal_sets"&&(
          <div>
            {/* Signal Sets Table */}
      <div style={{...card, marginBottom:16}}>
        <div style={{ padding:"16px 24px", borderBottom:"1px solid var(--border-subtle)", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ fontSize:13, fontWeight:800, color:"var(--text-main)" }}>Your Signal Sets <span style={{color:"var(--text-muted)",fontWeight:400}}>{signalSets.length}/5</span></div>
            <div style={{ fontSize:11, color:"var(--text-muted)", marginTop:2 }}>Investors subscribe to your signal sets</div>
          </div>
        </div>
        {!hasSignalSets?(
          <div style={{maxWidth:520,margin:"0 auto",padding:"28px 24px"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:17,fontWeight:700,color:"var(--text-main)",marginBottom:6}}>Welcome, Plan Provider </div>
              <div style={{fontSize:13,color:"var(--text-muted)"}}>Start publishing signals in 3 easy steps.</div>
            </div>
            {[
              { n:1, title:"Create a Signal Set", desc:"A Signal Set is your brand as a signal provider. Give it a name and strategy description. Max 5 signal sets.", action:true },
              { n:2, title:"Publish your first signal", desc:"Send trade ideas with entry, SL, TP, and direction. Investors subscribed to your set will receive them automatically." },
              { n:3, title:"Grow your subscribers", desc:"Your AlphaRank score is calculated from signal accuracy, risk management, and track record. Higher score = more visibility in Marketplace." },
            ].map(s => (
              <div key={s.n} style={{display:"flex",gap:16,marginBottom:14,
                background:"var(--bg-surface)",border:"1px solid var(--border)",
                borderRadius:"var(--radius-xl)",padding:"14px 18px",
                borderLeft:s.n===1?"3px solid var(--accent)":"3px solid var(--border)"}}>
                <div style={{width:30,height:30,borderRadius:"50%",flexShrink:0,
                  background:s.n===1?"var(--accent)":"var(--bg-elevated)",
                  border:"1px solid var(--border)",
                  display:"flex",alignItems:"center",justifyContent:"center",
                  fontSize:12,fontWeight:700,color:s.n===1?"#fff":"var(--text-muted)"}}>
                  {s.n}
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)",marginBottom:4}}>{s.title}</div>
                  <div style={{fontSize:12,color:"var(--text-muted)",lineHeight:1.6}}>{s.desc}</div>
                  {s.action && (
                    <button onClick={()=>setShowCreateSet(true)} style={{marginTop:8,padding:"6px 16px",
                      background:"var(--accent)",color:"#fff",border:"none",
                      borderRadius:"var(--radius-md)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                      + Create Signal Set
                    </button>
                  )}
                </div>
              </div>
            ))}
            <div style={{background:"var(--accent-soft)",border:"1px solid var(--accent-border)",
              borderRadius:"var(--radius-xl)",padding:"12px 18px",fontSize:12,color:"var(--accent)",textAlign:"center"}}>
                 Your signals are ranked by accuracy and risk. Build track record to attract more investors.
            </div>
          </div>
        ):(
          <div style={{overflowX:"auto"}}><table style={{ width:"100%", borderCollapse:"collapse", minWidth:500 }}>
            <thead><tr style={{ background:"var(--bg-elevated-soft)" }}>
              <TH>Signal Set</TH><TH>Age</TH><TH>Virtual Score</TH><TH>Auto Followers</TH><TH>Win Rate</TH><TH>Profit Factor</TH><TH>Status</TH><TH>Action</TH>
            </tr></thead>
            <tbody>
              {signalSets.map(s=>(
                <tr key={s.id} onMouseEnter={e=>e.currentTarget.style.background="var(--bg-elevated-soft)"} onMouseLeave={e=>e.currentTarget.style.background=""}>
                  <TD><div style={{ fontWeight:700 }}>{s.name}</div><div style={{ fontSize:10, color:"var(--text-muted)", fontFamily:"monospace", marginTop:2 }}>{s.id}</div></TD>
                  <TD style={{color:"var(--text-muted)",fontSize:12}}>{(()=>{const d=s.daysActive||0;return d>0?(d>=30?`${Math.floor(d/30)}mo ${d%30}d`:`${d}d`):"—";})()}</TD>
                                    <TD>{(() => {
                    const same = String(s.id) === String(selectedSetId);
                    const useVa = same && virtualAlpha && (virtualAlpha.alphaScore != null);
                    const g = useVa ? virtualAlpha.grade : s.grade;
                    const sc = useVa ? Number(virtualAlpha.alphaScore) : Number(s.alphaScore || 0);
                    const closed = s.closedSignals || 0;
                    if (useVa && sc > 0)
                      return <span style={{fontWeight:800,color:GRADE_COLOR[g]||"var(--accent)"}} title="Virtual (same as Overview)">{g} {sc.toFixed(1)}</span>;
                    if (sc > 0 && closed >= 20)
                      return <span style={{fontWeight:800,color:GRADE_COLOR[g]||"var(--accent)"}} title="Stored set score">{g} {sc.toFixed(1)}</span>;
                    return <span style={{color:"var(--text-muted)",fontSize:11}}>{closed}/20</span>;
                  })()}</TD>
                  <TD><span style={{ color:s.autoFollowers>0?"var(--success)":"var(--text-muted)", fontWeight:700 }}>{s.autoFollowers??0}</span></TD>
                  <TD>{s.winRate?<span style={{ color:"var(--text-main)", fontWeight:700 }}>{fmtPct(s.winRate||0)}</span>:<span style={{ color:"var(--text-muted)" }}>—</span>}</TD>
                  <TD>{s.profitFactor?<span style={{ color:"var(--text-main)", fontWeight:700 }}>{fmt2(s.profitFactor||0)}</span>:<span style={{ color:"var(--text-muted)" }}>—</span>}</TD>
                  <TD><span style={pill(s.status==="Active"?{bg:"rgba(29,209,161,0.15)",color:"var(--success)"}:{bg:"rgba(140,146,176,0.12)",color:"var(--text-muted)"})}>{s.status}</span></TD>
                  <TD>
                    <div style={{display:"flex",gap:6,alignItems:"center"}}>
                      {!readOnly && <button style={btnSmall(false)} onClick={()=>setEditingSet(s)}>Edit</button>}
                      {!readOnly && <button style={{...btnSmall(true),opacity:0.75}} onClick={async()=>{
                        if(!window.confirm(`Delete signal set "${s.name}"?\n\nSemua signal history akan ikut terhapus. Aksi ini tidak bisa di-undo.`)) return;
                        try{
                          await api.deleteAnalystSignalSet(s.id);
                          fetchData();
                        }catch(e){ alert("Gagal: "+e.message); }
                      }}>Delete</button>}
                    </div>
                  </TD>
                  <TD>
                    <button style={{...btnSmall(false),background:"var(--accent-soft)",color:"var(--accent)",border:"1px solid var(--accent-border)"}}
                      onClick={()=>{
                        const tok=localStorage.getItem("access_token")||localStorage.getItem("token")||"";
                        const base=`${window.location.origin}/strategy/analyst/${s.id}`;
                        fetch("/api/investor/affiliate/overview",{headers:{Authorization:`Bearer ${tok}`}})
                          .then(r=>r.json())
                          .then(d=>{
                            const ref=d?.affiliate?.code||"";
                            const url=ref?`${base}?ref=${ref}`:base;
                            navigator.clipboard.writeText(url);
                            alert("Link copied!\n\n"+url);
                          })
                          .catch(()=>{ navigator.clipboard.writeText(base); alert("Link copied!\n\n"+base); });
                      }}>
                      Share
                    </button>
                  </TD>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
          </div>
        )}
        {tab==="performance"&&(
          <div style={{padding:"24px", display:"flex", flexDirection:"column", gap:16}}>
            <AnalystPerformanceTab virtualAlpha={virtualAlpha} vaLoading={vaLoading} alphaRank={alphaRank} riskPct={riskPct} onRiskChange={setRiskPct}/>
            <SummaryStats alphaRank={alphaRank} signals={filteredSignals}/>
          </div>
        )}
        {tab==="active"&&<SignalTable signals={pendingSignals} showActions={true} onEdit={setEditingSig} onCancel={handleCancel} cancelId={cancelId}/>}
        {tab==="running"&&(runningSignals.length===0?<div style={{ padding:"24px 16px", textAlign:"center", color:"var(--text-muted)" }}><div style={{ fontSize:13, fontWeight:600 }}>No running signals</div></div>:<SignalTable signals={runningSignals} showActions={false}/>)}
        {tab==="history"&&(historySignals.length===0?<div style={{ padding:"24px 16px", textAlign:"center", color:"var(--text-muted)" }}><div style={{ fontSize:13, fontWeight:600 }}>No signal history yet</div></div>:<SignalTable signals={historySignals} showActions={false}/>)}


        {tab==="settings" && signalSets.length > 0 && (
          <SettingsTab
            signalSet={signalSets.find(s => s.id === selectedSetId) || signalSets[0]}
            onSaved={() => fetchData()}
          />
        )}
        {tab==="subscribers"&&(
          <div style={{padding:"12px 0"}}>
            {!subscribers ? (
              <div style={{textAlign:"center",color:"var(--text-muted)",padding:24}}>Loading followers...</div>
            ) : (
              <div>
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginBottom:14}}>
                  {[{label:"TOTAL FOLLOWERS",value:subscribers.totalFollowers??0,color:"var(--text-main)"},{label:"ACTIVE FOLLOWERS",value:subscribers.activeFollowers??0,color:"var(--success)"},{label:"TOTAL AUM",value:fmtK(subscribers.totalAUM??0),color:"var(--accent)"}].map(c=>(
                    <div key={c.label} style={{background:"var(--bg-elevated-soft)",border:"1px solid var(--border-subtle)",borderRadius:"var(--radius-md)",padding:"12px 14px"}}>
                      <div style={{fontSize:10,fontWeight:700,color:"var(--text-muted)",letterSpacing:0.06,textTransform:"uppercase",marginBottom:4}}>{c.label}</div>
                      <div style={{fontSize:20,fontWeight:700,color:c.color}}>{c.value}</div>
                    </div>
                  ))}
                </div>
                {subscribers.subscribers.length===0 ? (
                  <div style={{textAlign:"center",color:"var(--text-muted)",padding:24}}><div style={{fontSize:13,fontWeight:600}}>No followers yet</div></div>
                ) : (
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"var(--bg-elevated-soft)",borderBottom:"1px solid var(--border-subtle)"}}>
                        {["#","Account","Broker","Signal Set","Status","AUM","Signals","Wins","Win Rate","Since"].map(h=>(<th key={h} style={{padding:"8px 10px",textAlign:"left",fontWeight:700,fontSize:10,color:"var(--text-muted)",textTransform:"uppercase",letterSpacing:0.8}}>{h}</th>))}
                      </tr></thead>
                      <tbody>
                        {subscribers.subscribers.map((s,i)=>{
                          const wr=s.totalSignals>0?((s.wins/s.totalSignals)*100).toFixed(1)+"%":"—";
                          const isActive=s.status==="ACTIVE";
                          return(
                            <tr key={s.id} style={{borderBottom:"1px solid var(--border-subtle)"}}>
                              <td style={{padding:"8px 10px",color:"var(--text-muted)",fontSize:11}}>#{i+1}</td>
                              <td style={{padding:"8px 10px",fontWeight:700}}>{s.accountName||"—"}</td>
                              <td style={{padding:"8px 10px",color:"var(--text-muted)",fontSize:12}}>{s.broker||"—"}</td>
                              <td style={{padding:"8px 10px",fontWeight:600,fontSize:12}}>{s.setName}</td>
                              <td style={{padding:"8px 10px"}}><span style={{padding:"2px 8px",borderRadius:4,fontSize:10,fontWeight:800,background:isActive?"rgba(29,209,161,0.15)":"rgba(248,113,113,0.15)",color:isActive?"var(--success)":"var(--danger)"}}>{s.status}</span></td>
                              <td style={{padding:"8px 10px",fontWeight:700,color:s.aum>0?"var(--accent)":"var(--text-muted)"}}>${fmt(s.aum??0,2)}</td>
                              <td style={{padding:"8px 10px",textAlign:"center"}}>{s.totalSignals}</td>
                              <td style={{padding:"8px 10px",color:"var(--success)",textAlign:"center",fontWeight:700}}>{s.wins}</td>
                              <td style={{padding:"8px 10px",fontWeight:700,color:"var(--text-main)"}}>{wr}</td>
                              <td style={{padding:"8px 10px",color:"var(--text-muted)",fontSize:11}}>{s.createdAt?.slice(0,10)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AnalystDashboard;
